import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '../LoopinoDesktopGuidelines.css';
import './DesktopAIGeneratorPage.css';
import useApi from '../../../hooks/useApi';
import useLanguage from '../../../hooks/useLanguage';
import useCampaign from '../../../hooks/useCampaign';
import useCompany from '../../../hooks/useCompany';
import DesktopCampaignSelector from '../DesktopCampaignSelector/DesktopCampaignSelector';
import DesktopImageGalleryPage from '../DesktopImageGallery/DesktopImageGalleryPage';
import { DesktopAlertProvider } from '../common/alerts/DesktopAlertProvider';
import { useDesktopAlert } from '../common/alerts/DesktopAlertContext';
import DesktopPostMediaManager from '../common/media/DesktopPostMediaManager';
import { Skeleton } from '../../effects';
import AIRegenerationModal from '../../Mobile/MobilePostReview/AIRegenerationModal';
import placeholderImage from '../../../assets/images/placeholder-post-image.png';
import iconImage from '../../../assets/images/icons/icon-image.svg';
import iconUpload from '../../../assets/images/icons/icon-upload.svg';
import iconAi from '../../../assets/images/icons/icon-ai.svg';
import iconVideo from '../../../assets/images/icons/icon-video-generator.svg';
import iconDelete from '../../../assets/images/icons/icon-delete.svg';
import iconCalendar from '../../../assets/images/icons/icon-calendar.svg';
import iconClock from '../../../assets/images/icons/icon-clock.svg';
import useHoldToRevealPreviousImage from '../common/hooks/useHoldToRevealPreviousImage';

const BackIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 12H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ScheduleCalendarIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="white" strokeWidth="2" />
    <line x1="16" y1="2" x2="16" y2="6" stroke="white" strokeWidth="2" />
    <line x1="8" y1="2" x2="8" y2="6" stroke="white" strokeWidth="2" />
    <line x1="3" y1="10" x2="21" y2="10" stroke="white" strokeWidth="2" />
  </svg>
);

const ScheduleClockIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
    <polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const ChevronDownIcon = ({ size = 18, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AIIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 18 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.56791 18.8123V17.9563H1.71194C1.23925 17.9563 0.856061 17.573 0.855972 17.1003C0.855972 16.6276 1.2392 16.2444 1.71194 16.2444H2.56791V15.3884C2.56791 14.9157 2.95119 14.5325 3.42389 14.5324C3.89666 14.5324 4.27986 14.9156 4.27986 15.3884V16.2444H5.13583C5.6086 16.2444 5.9918 16.6276 5.9918 17.1003C5.99171 17.573 5.60855 17.9563 5.13583 17.9563H4.27986V18.8123C4.27983 19.285 3.89664 19.6683 3.42389 19.6683C2.95121 19.6682 2.56794 19.285 2.56791 18.8123ZM10.4767 4.80819C10.6996 4.77883 10.927 4.79315 11.1455 4.85199H11.1446C11.5422 4.95852 11.8004 5.23185 11.9627 5.47207C12.1242 5.71114 12.2481 6.00066 12.3529 6.29619C12.5623 6.88697 12.7703 7.72584 13.0287 8.7586L13.1272 9.12687C13.1277 9.1282 13.1278 9.12959 13.1282 9.13085C13.1298 9.13146 13.1315 9.13219 13.1332 9.13284C13.2028 9.15873 13.3021 9.18814 13.4955 9.24332L14.7028 9.59467C15.0616 9.70418 15.3747 9.80788 15.6344 9.91118C15.8954 10.015 16.1552 10.1383 16.3729 10.2984C16.5953 10.4619 16.832 10.7075 16.9343 11.0667L16.9632 11.1822C17.0206 11.4541 17.0108 11.7369 16.9343 12.0053C16.832 12.3646 16.5953 12.6102 16.3729 12.7737C16.1552 12.9337 15.8954 13.0571 15.6344 13.1609C15.3747 13.2642 15.0616 13.3679 14.7028 13.4774L13.4955 13.8287C13.3017 13.884 13.2026 13.9134 13.1332 13.9392C13.1316 13.9398 13.1297 13.9396 13.1282 13.9402C13.1277 13.9417 13.1277 13.9436 13.1272 13.9452C13.1037 14.0159 13.0776 14.1158 13.0287 14.3125V14.3135C12.7703 15.3462 12.5623 16.1851 12.3529 16.7759C12.2482 17.0712 12.124 17.36 11.9627 17.599C11.8004 17.8393 11.5424 18.1135 11.1446 18.2201L11.1436 18.2191C10.8524 18.2974 10.5448 18.2978 10.2537 18.2191V18.2181C9.85746 18.1108 9.59965 17.839 9.43758 17.599C9.27623 17.36 9.15315 17.0703 9.04842 16.7749C8.83905 16.1842 8.62996 15.3461 8.3716 14.3135V14.3125C8.32287 14.1171 8.29666 14.0169 8.27306 13.9462C8.27238 13.9441 8.27171 13.9421 8.27107 13.9402C8.26992 13.9398 8.26831 13.9397 8.26709 13.9392C8.19746 13.9134 8.09824 13.8838 7.9048 13.8287H7.9038C7.01716 13.5751 6.28619 13.3673 5.76686 13.1609C5.50565 13.057 5.24523 12.9337 5.02734 12.7737C4.80497 12.6104 4.56776 12.3654 4.46499 12.0063V12.0053C4.37762 11.6987 4.37762 11.3734 4.46499 11.0667V11.0657L4.50978 10.9364C4.62696 10.6467 4.83277 10.4413 5.02734 10.2984C5.24523 10.1383 5.50565 10.015 5.76686 9.91118C6.28619 9.70471 7.01714 9.49701 7.9038 9.24332L8.26709 9.13284C8.26847 9.13233 8.26978 9.13134 8.27107 9.13085C8.27152 9.12954 8.2726 9.12826 8.27306 9.12687C8.29659 9.05618 8.32263 8.95638 8.3716 8.7596V8.7586L8.72792 7.36118C8.83852 6.94875 8.94375 6.5915 9.04842 6.29619C9.15315 6.00074 9.27615 5.71108 9.43758 5.47207C9.59972 5.23206 9.85767 4.95878 10.2547 4.85199L10.4767 4.80819Z" fill="currentColor"/>
  </svg>
);

const AILoadingOverlay = ({ text = '' }) => (
  <div className="loopino-image-ai-loading">
    <div className="loopino-image-ai-loading-dim" />
    <div className="loopino-image-ai-loading-content">
      <div className="loopino-image-ai-icon">
        <AIIcon size={22} />
        <div className="loopino-image-ai-pulse" />
      </div>
      <p className="loopino-image-ai-text">
        {text}
        <span className="loopino-image-ai-dots">
          <span className="dot">.</span>
          <span className="dot">.</span>
          <span className="dot">.</span>
        </span>
      </p>
    </div>
  </div>
);

const PROMPT_KEY_ALIASES = {
  soggetto: 'subject',
  subject: 'subject',
  composizione: 'composition',
  composition: 'composition',
  stile: 'style',
  style: 'style',
  camera: 'camera',
  luce: 'lighting',
  lighting: 'lighting',
  illuminazione: 'lighting',
  tono: 'tone',
  tone: 'tone',
  qualita: 'quality',
  qualità: 'quality',
  quality: 'quality',
  colori: 'color_palette',
  palette: 'color_palette',
  palette_colori: 'color_palette',
  mood: 'mood',
  atmosfera: 'mood',
  notes: 'additional_notes',
  note: 'additional_notes',
  note_aggiuntive: 'additional_notes',
  altri_dettagli: 'additional_notes',
  'additional notes': 'additional_notes',
  additional_notes: 'additional_notes',
  detagli: 'additional_notes',
  metadata: 'metadata',
  instructions: 'instructions'
};

const PROMPT_JSON_KEY_ORDER = [
  'subject',
  'composition',
  'style',
  'camera',
  'lighting',
  'mood',
  'tone',
  'quality',
  'color_palette',
  'additional_notes'
];

const RESERVED_PROMPT_JSON_KEYS = new Set(['metadata', 'reference_images', 'instructions']);

const PROMPT_LABEL_DEFAULTS = {
  en: {
    subject: 'Subject',
    composition: 'Composition',
    style: 'Style',
    camera: 'Camera',
    lighting: 'Lighting',
    mood: 'Mood',
    tone: 'Tone',
    quality: 'Quality',
    color_palette: 'Color palette',
    additional_notes: 'Additional notes'
  },
  it: {
    subject: 'Soggetto',
    composition: 'Composizione',
    style: 'Stile',
    camera: 'Camera',
    lighting: 'Illuminazione',
    mood: 'Atmosfera',
    tone: 'Tono',
    quality: 'Qualità',
    color_palette: 'Palette colori',
    additional_notes: 'Note aggiuntive'
  }
};

const PROMPT_MIN_LENGTH = 10;
const PROMPT_MAX_LENGTH = 1000;
const MAX_REFERENCE_IMAGES = 3;
const MAX_IMAGE_UPLOAD_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_IMAGE_UPLOAD_SIZE_MB = Math.round(MAX_IMAGE_UPLOAD_SIZE_BYTES / (1024 * 1024));
const VIDEO_PROMPT_MIN_LENGTH = 1;
const VIDEO_PROMPT_MAX_LENGTH = 2000;
const VIDEO_RATIO_OPTIONS = ['1:1', '4:5', '5:4', '16:9', '9:16'];
const VIDEO_DEFAULT_RESOLUTION = '720p';
const VIDEO_RESOLUTION_OPTIONS = ['720p', '1080p', '2k', '4k'];
const VIDEO_FPS_OPTIONS = [
  { value: 24, label: '24fps' },
  { value: 30, label: '30fps' },
  { value: 60, label: '60fps' }
];
const VIDEO_DURATION_OPTIONS = Array.from({ length: 30 }, (_, idx) => idx + 1);
const VIDEO_COUNT_OPTIONS = [1, 2, 3, 4];
const TEMP_VIDEO_REVIEW_MODE = true;

const formatBytes = (bytes, decimals = 1) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.min(sizes.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
  return `${Number.parseFloat((bytes / k ** i).toFixed(dm))}${sizes[i]}`;
};

const DualOptionToggle = ({
  active = false,
  onChange,
  leftLabel,
  rightLabel,
  disabled = false,
  ariaLabel = '',
  className = ''
}) => (
  <button
    type="button"
    className={`dual-option-toggle ${active ? 'checked' : 'unchecked'} ${disabled ? 'disabled' : ''} ${className}`}
    onClick={() => {
      if (disabled) return;
      onChange?.(!active);
    }}
    aria-pressed={active}
    aria-label={ariaLabel || (active ? rightLabel : leftLabel)}
    disabled={disabled}
  >
    <span className="dual-option-toggle-track" aria-hidden="true" />
    <span className="dual-option-toggle-label dual-option-toggle-off">{leftLabel}</span>
    <span className="dual-option-toggle-label dual-option-toggle-on">{rightLabel}</span>
  </button>
);

const TripleOptionToggle = ({
  value = 'post',
  onChange,
  labels = [],
  disabled = false,
  ariaLabel = '',
  className = ''
}) => {
  const options = labels.length === 3
    ? labels
    : [
        { value: 'post', label: 'Post' },
        { value: 'image', label: 'Immagini' },
        { value: 'video', label: 'Video' }
      ];
  const normalizedValue = options.some((option) => option.value === value) ? value : options[0].value;
  const activeIndex = Math.max(0, options.findIndex((option) => option.value === normalizedValue));
  const trackStyle = { transform: `translateX(${activeIndex * 100}%)` };

  return (
    <div
      className={`triple-option-toggle ${disabled ? 'disabled' : ''} ${className}`}
      role="tablist"
      aria-label={ariaLabel || 'Seleziona una tab'}
    >
      <span className="triple-option-toggle-track" aria-hidden="true" style={trackStyle} />
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`triple-option-toggle-label ${normalizedValue === option.value ? 'active' : ''}`}
          onClick={() => {
            if (disabled) return;
            onChange?.(option.value);
          }}
          aria-pressed={normalizedValue === option.value}
          disabled={disabled}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};

const useAutosizeTextarea = (textareaRef, value) => {
  useLayoutEffect(() => {
    const el = textareaRef?.current;
    if (!el) return;

    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [textareaRef, value]);
};

const AutoGrowTextarea = ({ value, className = '', onChange, ...props }) => {
  const textareaRef = useRef(null);
  useAutosizeTextarea(textareaRef, value);

  return (
    <textarea
      {...props}
      ref={textareaRef}
      value={value}
      onChange={onChange}
      className={className}
    />
  );
};

const normalizeVideoCapabilityValue = (value) => `${value ?? ''}`.trim().toLowerCase();

const normalizeVideoFpsValue = (value) => {
  const parsed = parseInt(`${value ?? ''}`.replace(/[^\d]/g, ''), 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const getModelCapabilityList = (model, keys = []) => {
  if (!model || !keys.length) return null;
  const sources = [model.capabilities, model.metadata];
  for (const source of sources) {
    if (!source) continue;
    for (const key of keys) {
      const value = source?.[key];
      if (Array.isArray(value) && value.length) {
        return value;
      }
      if (value && Array.isArray(value.values) && value.values.length) {
        return value.values;
      }
      if (value && Array.isArray(value.options) && value.options.length) {
        return value.options;
      }
    }
  }
  return null;
};

const useDNADropdown = () => {
  const [open, setOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  useLayoutEffect(() => {
    if (triggerRef.current) {
      setMenuWidth(triggerRef.current.offsetWidth);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    const handleKey = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return {
    open,
    setOpen,
    menuWidth,
    triggerRef,
    menuRef
  };
};

const DNADropdown = ({
  value,
  options = [],
  onChange,
  placeholder = 'Seleziona',
  disabled = false,
  className = '',
  ariaLabel = ''
}) => {
  const {
    open,
    setOpen,
    menuWidth,
    triggerRef,
    menuRef
  } = useDNADropdown();
  const selectedOption = options.find((option) => option.value === value);
  const label = selectedOption?.label || placeholder;

  useEffect(() => {
    if (disabled && open) {
      setOpen(false);
    }
  }, [disabled, open, setOpen]);

  return (
    <div className={`loopino-dna-dropdown ${className}`} data-open={open || undefined}>
      <button
        type="button"
        className="loopino-dna-dropdown-trigger"
        ref={triggerRef}
        onClick={() => {
          if (disabled) return;
          setOpen((prev) => !prev);
        }}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel || label}
        disabled={disabled}
      >
        <span>{label}</span>
        <ChevronDownIcon className="loopino-dna-dropdown-chevron" />
      </button>
      {open && (
        <div
          className="loopino-dna-dropdown-menu"
          ref={menuRef}
          role="listbox"
          style={{ minWidth: menuWidth || undefined }}
        >
          {options.map((option) => (
            <button
              type="button"
              key={`${option.value}`}
              className="loopino-dna-dropdown-item"
              data-selected={option.value === value || undefined}
              aria-selected={option.value === value}
              role="option"
              disabled={option.disabled}
              onClick={() => {
                if (option.disabled) return;
                onChange?.(option.value);
                setOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const useEstimatedProgressPercent = (isActive, durationMs = 90000, capPercent = 99) => {
  const [percent, setPercent] = useState(0);
  const startAtRef = useRef(null);

  useEffect(() => {
    if (!isActive) {
      startAtRef.current = null;
      setPercent(0);
      return undefined;
    }

    startAtRef.current = Date.now();
    setPercent(0);

    const update = () => {
      const startAt = startAtRef.current;
      if (!startAt) {
        setPercent(0);
        return;
      }
      const elapsed = Math.max(0, Date.now() - startAt);
      const ratio = durationMs > 0 ? Math.min(1, elapsed / durationMs) : 1;
      const next = Math.min(capPercent, Math.floor(ratio * capPercent));
      setPercent((prev) => (prev === next ? prev : next));
    };

    update();
    const intervalId = setInterval(update, 250);
    return () => clearInterval(intervalId);
  }, [capPercent, durationMs, isActive]);

  return percent;
};

const EstimatedProgressBar = ({ label, percent, className = '', ariaLabel = '' }) => {
  const safePercent = Math.min(99, Math.max(0, Number.isFinite(percent) ? percent : 0));
  return (
    <div
      className={`loopino-estimated-progress ${className}`.trim()}
      role="progressbar"
      aria-label={ariaLabel || label}
      aria-valuemin={0}
      aria-valuemax={99}
      aria-valuenow={safePercent}
    >
      <div className="loopino-estimated-progress-fill" style={{ width: `${safePercent}%` }} />
      <div className="loopino-estimated-progress-meta">
        <span className="loopino-estimated-progress-label">{label}</span>
        <span className="loopino-estimated-progress-percent">{safePercent}%</span>
      </div>
    </div>
  );
};

const generateMediaId = () => `desktop-media-${Date.now().toString(36)}-${Math.random().toString(16).slice(2)}`;

const splitPromptIntoBlocks = (prompt) => {
  if (!prompt) return [];
  const byLines = prompt.split('\n').map((p) => p.trim()).filter(Boolean);
  if (byLines.length > 1) return byLines;
  const bySentence = prompt.split(/(?<=[.!?])\s+/).map((p) => p.trim()).filter(Boolean);
  if (bySentence.length > 1) return bySentence;
  return [prompt.trim()];
};

const PROMPT_BLOCK_REGEX = /^\s*([^:]{1,80}):( ?)([\s\S]*)$/;

const normalizeLabelToKey = (label, fallbackIndex = 0) => {
  if (!label) {
    return `section_${fallbackIndex + 1}`;
  }

  const normalized = label
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s_-]/g, '')
    .replace(/\s+/g, '_');

  if (!normalized) {
    return `section_${fallbackIndex + 1}`;
  }

  return PROMPT_KEY_ALIASES[normalized] || normalized;
};

const parsePromptBlock = (block) => {
  const trimmed = typeof block === 'string' ? block.trim() : '';
  if (!trimmed) {
    return { label: '', value: '' };
  }

  const match = PROMPT_BLOCK_REGEX.exec(trimmed);
  if (match) {
    return {
      label: match[1].trim(),
      value: match[3].trim()
    };
  }

  return {
    label: '',
    value: trimmed
  };
};

const buildPromptJsonFromBlocks = (blocks = [], options = {}) => {
  const {
    normalizedRatio,
    quality,
    tone,
    creativity,
    simplePrompt,
    referenceImages = [],
    mode,
    baseReferenceIncluded = false,
    includeReferenceStats = false
  } = options;

  const promptJson = {};

  blocks.forEach((block, index) => {
    const { label, value } = parsePromptBlock(block);
    if (!value) {
      return;
    }
    const key = normalizeLabelToKey(label || `section_${index + 1}`, index);
    if (!key || RESERVED_PROMPT_JSON_KEYS.has(key)) {
      return;
    }
    promptJson[key] = value;
  });

  const metadata = {};
  if (normalizedRatio) metadata.aspect_ratio = normalizedRatio;
  if (quality) metadata.quality = quality;
  if (tone) metadata.tone = tone;
  const normalizedCreativity = typeof creativity === 'number' ? Math.min(1, Math.max(0, parseFloat(creativity))) : undefined;
  if (normalizedCreativity !== undefined && !Number.isNaN(normalizedCreativity)) {
    metadata.creativity = normalizedCreativity;
  }
  if (mode) metadata.mode = mode;
  if (includeReferenceStats) {
    if (typeof baseReferenceIncluded === 'boolean') {
      metadata.base_reference_included = baseReferenceIncluded;
    }
    if (Array.isArray(referenceImages)) {
      metadata.reference_images_supplied = referenceImages.length;
    }
  }

  if (Object.keys(metadata).length > 0) {
    promptJson.metadata = metadata;
  }

  if (Array.isArray(referenceImages) && referenceImages.length > 0) {
    promptJson.reference_images = referenceImages;
  }

  if (simplePrompt) {
    promptJson.instructions = simplePrompt;
  }

  return promptJson;
};

const buildPromptJsonSchema = (promptJson = {}) => {
  const schema = {};
  Object.keys(promptJson).forEach((key) => {
    if (RESERVED_PROMPT_JSON_KEYS.has(key)) {
      return;
    }
    schema[key] = null;
  });
  return Object.keys(schema).length > 0 ? schema : null;
};

const buildBlocksFromPromptJson = (promptJson, resolveLabel) => {
  if (!promptJson || typeof promptJson !== 'object') {
    return [];
  }

  const blocks = [];
  const visited = new Set();

  const appendBlock = (key) => {
    if (!key || visited.has(key) || RESERVED_PROMPT_JSON_KEYS.has(key)) {
      return;
    }

    const value = promptJson[key];
    if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) {
      return;
    }

    let content = value;
    if (typeof value === 'object') {
      try {
        content = JSON.stringify(value);
      } catch (error) {
        content = String(value);
      }
    }

    const label = resolveLabel ? resolveLabel(key) : key;
    blocks.push(`${label}: ${content}`);
    visited.add(key);
  };

  PROMPT_JSON_KEY_ORDER.forEach(appendBlock);
  Object.keys(promptJson).forEach((key) => appendBlock(key));
  return blocks;
};

const resolveDnaId = (dna) => {
  if (!dna) return null;
  return dna.id ?? dna._id ?? dna.dna_id ?? dna.value ?? dna.key ?? null;
};

const resolveMediaFileId = (media) => {
  if (!media) return null;
  const candidates = [
    media.mediaFileId,
    media.media_file_id,
    media.mediaId,
    media.media_id,
    media.file_id,
    media.fileId,
    media.raw?.mediaFileId,
    media.raw?.media_file_id,
    media.raw?.mediaId,
    media.raw?.media_id,
    media.raw?.id,
    media.raw?._id
  ];

  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null) continue;
    const value = `${candidate}`.trim();
    if (value) return value;
  }

  const hasLocalFile = media?.file instanceof File || media?.file instanceof Blob;
  if (hasLocalFile) {
    return null;
  }

  const directId = media.id ?? media._id;
  if (directId !== undefined && directId !== null) {
    const value = `${directId}`.trim();
    if (value && !value.startsWith('media-') && !value.startsWith('desktop-media-') && !value.startsWith('generated-')) {
      return value;
    }
  }

  return null;
};

const toAbsoluteHttpUrl = (value) => {
  if (!value) return null;
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : undefined;
    const parsed = new URL(value, base);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return parsed.href;
    }
    return null;
  } catch (error) {
    console.warn('[DesktopAIGeneratorPage] Skipping invalid reference url', value, error);
    return null;
  }
};

const normalizeStrategyItems = (items = [], t) =>
  items
    .map((item) => ({
      id: item?.id ?? item?.value ?? item?.key,
      label: item?.name ?? item?.label ?? item?.title ?? t('imageGenerator.settings.strategyFallback', { defaultValue: 'Strategia' }),
      description: item?.description ?? item?.subtitle ?? ''
    }))
    .filter((item) => item.id !== undefined && item.id !== null);

const normalizeDnaItems = (items = [], t) =>
  items
    .map((item) => ({
      id: resolveDnaId(item),
      label: item?.name ?? item?.label ?? item?.title ?? t('imageGenerator.settings.dnaFallback', { defaultValue: 'DNA' }),
      description: item?.description ?? item?.subtitle ?? ''
    }))
    .filter((item) => item.id !== undefined && item.id !== null);

const normalizeParamId = (value) => {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string' || typeof value === 'number') {
    const normalized = `${value}`.trim();
    return normalized ? normalized : null;
  }
  if (typeof value === 'object') {
    const nested = value.id ?? value._id ?? value.value ?? value.key;
    return normalizeParamId(nested);
  }
  return null;
};

const normalizeGeneratorTab = (value) => {
  if (!value) return null;
  const normalized = `${value}`.trim().toLowerCase();
  if (normalized === 'post' || normalized === 'image' || normalized === 'video') {
    return normalized;
  }
  return null;
};

const parseGeneratorBoolean = (value) => {
  if (value === true || value === false) return value;
  if (value === 1 || value === 0) return Boolean(value);
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  if (['1', 'true', 'yes', 'on'].includes(normalized)) return true;
  if (['0', 'false', 'no', 'off'].includes(normalized)) return false;
  return null;
};

const resolveGeneratorNavigationState = ({ queryString = '', routeState = null, initialTab = 'post' } = {}) => {
  let params = null;
  try {
    if (queryString) {
      params = new URLSearchParams(queryString);
    } else if (typeof window !== 'undefined') {
      params = new URLSearchParams(window.location.search || '');
    }
  } catch (error) {
    params = null;
  }

  const tab = normalizeGeneratorTab(
    routeState?.tab ??
    routeState?.initialTab ??
    params?.get('tab') ??
    initialTab
  ) || 'post';

  const campaignId = normalizeParamId(
    routeState?.campaignId ??
    routeState?.targetCampaignId ??
    params?.get('campaignId') ??
    params?.get('targetCampaignId')
  );

  const strategyId = normalizeParamId(routeState?.strategyId ?? params?.get('strategyId'));
  const dnaId = normalizeParamId(routeState?.dnaId ?? params?.get('dnaId'));

  const useCompanyDna = parseGeneratorBoolean(routeState?.useCompanyDna ?? params?.get('useCompanyDna'));
  const useDna = parseGeneratorBoolean(routeState?.useDna ?? params?.get('useDna'));
  const useStrategy = parseGeneratorBoolean(routeState?.useStrategy ?? params?.get('useStrategy'));

  return {
    tab,
    campaignId,
    strategyId,
    dnaId,
    useCompanyDna,
    useDna,
    useStrategy
  };
};

const resolveCustomRatio = (widthValue, heightValue, fallback = '4:5') => {
  const width = parseInt(widthValue, 10);
  const height = parseInt(heightValue, 10);
  if (!width || !height) return fallback;
  const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
  const divisor = gcd(width, height);
  return `${Math.round(width / divisor)}:${Math.round(height / divisor)}`;
};

const mapRatioToSize = (ratio, customWidth, customHeight) => {
  if (ratio === 'custom') {
    const width = parseInt(customWidth, 10) || 1024;
    const height = parseInt(customHeight, 10) || 1024;
    return `${width}x${height}`;
  }
  const presets = {
    '1:1': '1024x1024',
    '4:5': '1024x1280',
    '5:4': '1280x1024',
    '16:9': '1280x720',
    '9:16': '720x1280',
    '4:3': '1200x900',
    '3:4': '900x1200',
    '21:9': '1600x685'
  };
  return presets[ratio] || '1024x1024';
};

const mergeUniqueById = (...lists) => {
  const map = new Map();
  lists.flat().filter(Boolean).forEach((item) => {
    if (!item || item.id === undefined || item.id === null) return;
    const key = `${item.id}`;
    map.set(key, { ...map.get(key), ...item });
  });
  return Array.from(map.values());
};

const extractDnaFromCompany = (company, t) => {
  if (!company) return null;
  const dnaSource = company.generated_dna || company.dna || company.ai_analysis?.company_identity;
  if (!dnaSource) return null;
  const description = typeof dnaSource === 'string'
    ? dnaSource
    : dnaSource.dna || dnaSource.mission || dnaSource.summary || JSON.stringify(dnaSource);
  return {
    id: company.id || company._id,
    label: company.name || t('imageGenerator.settings.dnaFallback', { defaultValue: 'DNA' }),
    description
  };
};

const extractStrategiesFromCampaigns = (campaignList = [], t) => {
  return campaignList
    .filter((campaign) => campaign?.strategy)
    .map((campaign) => ({
      id: campaign.id || campaign._id || campaign.value,
      label: campaign.name || campaign.title || t('imageGenerator.settings.strategyFallback', { defaultValue: 'Strategia' }),
      description: campaign.strategy?.desc || campaign.strategy?.description || campaign.strategy?.summary || ''
    }));
};

const deriveStrategyIdFromCampaign = (campaign) => {
  if (!campaign || !campaign.strategy) return null;
  return campaign.strategy.id || campaign.strategy._id || campaign.strategy.value || campaign.id || campaign._id || campaign.value || null;
};

const extractPlatformsFromCampaign = (campaign) => {
  if (!campaign) return [];
  if (Array.isArray(campaign.target_platforms) && campaign.target_platforms.length) return campaign.target_platforms;
  if (Array.isArray(campaign.platforms) && campaign.platforms.length) return campaign.platforms;
  if (campaign.platform) return [campaign.platform];
  if (Array.isArray(campaign.socialChannels) && campaign.socialChannels.length) return campaign.socialChannels;
  return [];
};

const normalizeHashtags = (hashtags) => {
  if (!Array.isArray(hashtags)) return [];
  return hashtags
    .map((h) => `${h || ''}`.trim())
    .filter(Boolean)
    .map((h) => (h.startsWith('#') ? h : `#${h}`));
};

const DesktopAIGeneratorContent = ({
  initialMode = 'fast',
  initialTab = 'post',
  defaultPrompt = '',
  initialSimplePrompt = '',
  availableStrategies = null,
  availableDnaProfiles = null,
  queryString = '',
  routeState = null,
  onNavigate = null
}) => {
  const { t, language } = useLanguage();
  const apiClient = useApi();
  const { notify } = useDesktopAlert();
  const {
    campaigns: fetchedCampaigns,
    loading: campaignsLoading
  } = useCampaign();
  const {
    currentCompany,
    companies: companyList,
    loading: companyLoading
  } = useCompany();

  const initialNavigation = resolveGeneratorNavigationState({ queryString, routeState, initialTab });

  const languageCode = useMemo(() => (
    typeof language === 'string' && language
      ? language.split('-')[0]?.toLowerCase() || 'en'
      : 'en'
  ), [language]);

  const resolvedLocale = useMemo(() => {
    if (language) {
      try {
        new Intl.DateTimeFormat(language);
        return language;
      } catch (error) {
        console.warn('[DesktopAIGeneratorPage] Falling back to it locale', { language, error });
      }
    }
    return 'it';
  }, [language]);

  const labelDefaults = useMemo(() => (
    PROMPT_LABEL_DEFAULTS[languageCode] || PROMPT_LABEL_DEFAULTS.en
  ), [languageCode]);

  const normalizedDefaultPrompt = useMemo(
    () => (defaultPrompt && defaultPrompt.trim()) || '',
    [defaultPrompt]
  );

  const promptTemplateLines = useMemo(() => {
    const value = t('imageGenerator.defaults.promptTemplate', { returnObjects: true });
    if (Array.isArray(value)) {
      return value.map((entry) => (typeof entry === 'string' ? entry : '')).filter(Boolean);
    }
    return [];
  }, [t, language]);

  const buildEmptyBlocksFromTemplate = useCallback(() => {
    if (!promptTemplateLines.length) {
      return [''];
    }

    return promptTemplateLines.map((line, lineIdx) => {
      const { label } = parsePromptBlock(line);
      const fallbackLabel = t('imageGenerator.pro.blockFallback', {
        index: lineIdx + 1,
        defaultValue: `Blocco ${lineIdx + 1}`
      });
      const safeLabel = label || fallbackLabel;
      return `${safeLabel}: `;
    });
  }, [promptTemplateLines, t]);

  const buildBlockPlaceholder = useCallback((label, idx) => {
    const normalizedKey = normalizeLabelToKey(label, idx);
    const examples = {
      subject: 'Es: Un cane con le gambe di coccodrillo',
      composition: 'Es: Inquadratura dall’alto su un picnic un po’ pasticciato',
      style: 'Es: Stile fumetto anni 90, colori super saturi',
      camera: 'Es: Scatto fish-eye da skater che fa una piega',
      lighting: 'Es: Luci al neon da discoteca anni 80',
      mood: 'Es: Atmosfera festa sorpresa con amici che ridono',
      tone: 'Es: Tono ironico ma curato, zero formalità',
      quality: 'Es: Alta definizione ma niente posa da catalogo',
      color_palette: 'Es: Palette gelato pistacchio, fragola e limone',
      additional_notes: 'Es: Niente gatti volanti stavolta (forse)',
      default: 'Es: Aggiungi qui cosa vuoi vedere generato'
    };
    return examples[normalizedKey] || examples.default;
  }, []);

  const [campaignOptions, setCampaignOptions] = useState(fetchedCampaigns || []);
  const [strategyOptions, setStrategyOptions] = useState(availableStrategies || []);
  const [dnaOptions, setDnaOptions] = useState(availableDnaProfiles || []);
  const [activeTab, setActiveTab] = useState(() => {
    if (initialNavigation.tab === 'video') return 'video';
    if (initialNavigation.tab === 'image') return 'image';
    return 'post';
  });
  const [imageMode, setImageMode] = useState(() => (initialMode === 'pro' ? 'pro' : 'fast'));
  const [postMode, setPostMode] = useState(TEMP_VIDEO_REVIEW_MODE ? 'manual' : 'auto');
  const showPostGenerationSettings = !TEMP_VIDEO_REVIEW_MODE;
  const [blocks, setBlocks] = useState(() => {
    const fromDefaultPrompt = splitPromptIntoBlocks(normalizedDefaultPrompt);
    if (fromDefaultPrompt.length) {
      return fromDefaultPrompt;
    }
    const templateBlocks = buildEmptyBlocksFromTemplate();
    return templateBlocks.length ? templateBlocks : [''];
  });
  const [simplePromptText, setSimplePromptText] = useState(() => (
    initialSimplePrompt && initialSimplePrompt.trim()
      ? initialSimplePrompt
      : normalizedDefaultPrompt
  ));
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [customWidth, setCustomWidth] = useState('1024');
  const [customHeight, setCustomHeight] = useState('1024');
  const [promptImages, setPromptImages] = useState([]);
  const promptImagesRef = useRef([]);
  const initialSelectionRef = useRef({ ...initialNavigation, applied: false });
  const referenceInputRef = useRef(null);
  const videoFirstFrameInputRef = useRef(null);
  const videoLastFrameInputRef = useRef(null);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [useGeneratedImageAsBase, setUseGeneratedImageAsBase] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [selectedImageModel, setSelectedImageModel] = useState('seedream');
  const [referenceError, setReferenceError] = useState('');
  const [isDraggingReference, setIsDraggingReference] = useState(false);
  const [showGallery, setShowGallery] = useState(null); // reference | video-first-frame | video-last-frame | manual
  const [showPromptBlocksModal, setShowPromptBlocksModal] = useState(false);
  const [isRegeneratingBlocks, setIsRegeneratingBlocks] = useState(false);

  const [videoSimplePromptText, setVideoSimplePromptText] = useState('');
  const [videoAspectRatio, setVideoAspectRatio] = useState('9:16');
  const [videoResolution, setVideoResolution] = useState(VIDEO_DEFAULT_RESOLUTION);
  const [videoDuration, setVideoDuration] = useState(4);
  const [videoFps, setVideoFps] = useState(24);
  const [videoCount, setVideoCount] = useState(1);
  const [videoFirstFrame, setVideoFirstFrame] = useState(null);
  const [videoLastFrame, setVideoLastFrame] = useState(null);
  const [videoReferenceError, setVideoReferenceError] = useState('');
  const [videoReferenceUploading, setVideoReferenceUploading] = useState(false);
  const [videoReferenceVideos, setVideoReferenceVideos] = useState([]);
  const [videoReferenceVideoInput, setVideoReferenceVideoInput] = useState('');
  const [videoModels, setVideoModels] = useState([]);
  const [videoModelsLoading, setVideoModelsLoading] = useState(false);
  const [videoModelsError, setVideoModelsError] = useState('');
  const [selectedVideoModel, setSelectedVideoModel] = useState('');
  const [generatedVideo, setGeneratedVideo] = useState(null);
  const [generatedVideos, setGeneratedVideos] = useState([]);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoJob, setVideoJob] = useState(null);
  const [videoStatus, setVideoStatus] = useState('');
  const [videoProgress, setVideoProgress] = useState(null);
  const [videoError, setVideoError] = useState('');
  const videoPollRef = useRef(null);
  const uploadsAlbumIdRef = useRef(null);
  const videoModelsLoadedRef = useRef(false);
  const videoModelsRequestRef = useRef(false);
  const videoModelsAttemptedRef = useRef(false);

  const [selectedStrategyId, setSelectedStrategyId] = useState(null);
  const [selectedDnaId, setSelectedDnaId] = useState(null);
  const [useDnaForImage, setUseDnaForImage] = useState(false);
  const [useStrategyForImage, setUseStrategyForImage] = useState(false);
  const [postPrompt, setPostPrompt] = useState('');
  const [useDnaForPost, setUseDnaForPost] = useState(false);
  const [useStrategyForPost, setUseStrategyForPost] = useState(TEMP_VIDEO_REVIEW_MODE ? false : true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [autoPostLoading, setAutoPostLoading] = useState(false);
  const [manualDescription, setManualDescription] = useState('');
  const [manualMedia, setManualMedia] = useState([]);
  const manualMediaUrlsRef = useRef([]);
  const [manualLoading, setManualLoading] = useState(false);
  const [manualAiModalOpen, setManualAIModalOpen] = useState(false);
  const [manualAiModalType, setManualAiModalType] = useState(null);
  const [descriptionLoading, setDescriptionLoading] = useState(false);
  const [manualImageGenerating, setManualImageGenerating] = useState(false);
  const [scheduleError, setScheduleError] = useState('');
  const [scheduledAt, setScheduledAt] = useState(null);
  const [isSchedulePickerOpen, setSchedulePickerOpen] = useState(false);
  const [isScheduleTimePickerOpen, setScheduleTimePickerOpen] = useState(false);
  const [scheduleSelectedDate, setScheduleSelectedDate] = useState(null);
  const [scheduleSelectedTime, setScheduleSelectedTime] = useState('12:00');
  const [schedulePickerMonth, setSchedulePickerMonth] = useState(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  });
  const scheduleToday = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }, []);
  const scheduleWeekdayLabels = useMemo(() => ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'], []);
  const scheduleTimeInputRef = useRef(null);
  const scheduleTimePanelRef = useRef(null);
  const [postValidation, setPostValidation] = useState('');

  const pageHeaderRef = useRef(null);
  const imageEditorGridRef = useRef(null);
  const imagePreviewPanelRef = useRef(null);
  const imagePreviewPlaceholderRef = useRef(null);
  const [previewStickyTop, setPreviewStickyTop] = useState(() => 36);
  const previewPanelStyle = useMemo(() => {
    if (!Number.isFinite(previewStickyTop)) return undefined;
    return { '--loopino-desktop-preview-sticky-top': `${previewStickyTop}px` };
  }, [previewStickyTop]);
  const [previewPlaceholderHeight, setPreviewPlaceholderHeight] = useState(0);
  const previewPlaceholderStyle = useMemo(
    () => ({ height: `${Math.max(0, Number(previewPlaceholderHeight) || 0)}px` }),
    [previewPlaceholderHeight]
  );
  const [previewDynamicStyle, setPreviewDynamicStyle] = useState({});
  const [previewPinMode, setPreviewPinMode] = useState('static'); // static | fixed | bottom
  const previewRafRef = useRef(null);

  const imageModelOptions = useMemo(() => ([
    { value: 'seedream', label: 'seedream' }
  ]), []);

  const videoModelOptions = useMemo(() => {
    const baseOptions = [
      { value: '', label: t('imageGenerator.video.settings.modelAuto', { defaultValue: 'Auto' }) }
    ];
    const modelOptions = videoModels
      .map((model) => {
        const name = model?.name || model?.id;
        if (!name) return null;
        const providerLabel = model.provider ? ` · ${model.provider}` : '';
        return { value: name, label: `${name}${providerLabel}` };
      })
      .filter(Boolean);
    return [...baseOptions, ...modelOptions];
  }, [t, videoModels]);

  const selectedVideoModelData = useMemo(() => {
    if (!selectedVideoModel) return null;
    return videoModels.find((model) => (
      model?.name === selectedVideoModel || model?.id === selectedVideoModel
    )) || null;
  }, [selectedVideoModel, videoModels]);

  const supportedVideoResolutions = useMemo(() => {
    const raw = getModelCapabilityList(selectedVideoModelData, [
      'resolutions',
      'resolution',
      'supported_resolutions',
      'supportedResolutions',
      'video_resolutions',
      'videoResolutions'
    ]);
    if (!Array.isArray(raw) || raw.length === 0) return null;
    const normalized = raw.map(normalizeVideoCapabilityValue).filter(Boolean);
    return normalized.length ? new Set(normalized) : null;
  }, [selectedVideoModelData]);

  const supportedVideoFps = useMemo(() => {
    const raw = getModelCapabilityList(selectedVideoModelData, [
      'fps',
      'frame_rates',
      'frameRates',
      'supported_fps',
      'supportedFps',
      'frame_rate'
    ]);
    if (!Array.isArray(raw) || raw.length === 0) return null;
    const normalized = raw
      .map((value) => normalizeVideoFpsValue(value))
      .filter((value) => value !== null);
    return normalized.length ? new Set(normalized) : null;
  }, [selectedVideoModelData]);

  const videoDurationOptions = useMemo(() => (
    VIDEO_DURATION_OPTIONS.map((value) => ({ value, label: `${value}s` }))
  ), []);

  const videoCountOptions = useMemo(() => (
    VIDEO_COUNT_OPTIONS.map((value) => ({ value, label: `${value}` }))
  ), []);

  useEffect(() => {
    promptImagesRef.current = promptImages;
  }, [promptImages]);

  useEffect(() => {
    if (!supportedVideoResolutions) return;
    const normalizedCurrent = normalizeVideoCapabilityValue(videoResolution);
    if (normalizedCurrent && supportedVideoResolutions.has(normalizedCurrent)) return;
    const fallback = VIDEO_RESOLUTION_OPTIONS.find((option) => (
      supportedVideoResolutions.has(normalizeVideoCapabilityValue(option))
    ));
    if (fallback) {
      setVideoResolution(fallback);
    }
  }, [supportedVideoResolutions, videoResolution]);

  useEffect(() => {
    if (!supportedVideoFps) return;
    const normalizedCurrent = normalizeVideoFpsValue(videoFps);
    if (normalizedCurrent !== null && supportedVideoFps.has(normalizedCurrent)) return;
    const fallback = VIDEO_FPS_OPTIONS.find((option) => supportedVideoFps.has(option.value));
    if (fallback) {
      setVideoFps(fallback.value);
    }
  }, [supportedVideoFps, videoFps]);

  const applyPreviewLayout = useCallback((nextMode, nextStyle, placeholderHeight) => {
    setPreviewPinMode((prevMode) => (prevMode === nextMode ? prevMode : nextMode));
    setPreviewDynamicStyle((prevStyle) => {
      const keys = ['position', 'top', 'left', 'width', 'height'];
      const isSame = keys.every((key) => prevStyle?.[key] === nextStyle?.[key]);
      return isSame ? prevStyle : nextStyle;
    });
    const nextHeight = Number.isFinite(placeholderHeight) ? placeholderHeight : 0;
    setPreviewPlaceholderHeight((prevHeight) => (prevHeight === nextHeight ? prevHeight : nextHeight));
  }, []);

  const recomputePreviewPosition = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const previewNode = imagePreviewPanelRef.current;
    const gridNode = imageEditorGridRef.current;
    const placeholderNode = imagePreviewPlaceholderRef.current;

    if (!previewNode || !gridNode || !placeholderNode || !['image', 'video'].includes(activeTab)) {
      applyPreviewLayout('static', { position: 'relative', width: '100%', height: 'auto' }, 0);
      return;
    }

    const stickyTop = Number.isFinite(previewStickyTop) ? previewStickyTop : 32;

    if (window.innerWidth <= 1100) {
      applyPreviewLayout('static', { position: 'relative', width: '100%', height: 'auto' }, 0);
      return;
    }

    const previewHeight = previewNode.offsetHeight || 0;
    if (!previewHeight) {
      applyPreviewLayout('static', { position: 'relative', width: '100%', height: 'auto' }, 0);
      return;
    }

    const placeholderRect = placeholderNode.getBoundingClientRect();
    const previewWidth = placeholderRect.width || previewNode.offsetWidth || 0;

    const containerRect = gridNode.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const scrollX = window.scrollX || window.pageXOffset || 0;
    const containerTop = containerRect.top + scrollY;
    const containerBottom = containerRect.bottom + scrollY;

    const startStick = containerTop - stickyTop;
    const endStick = containerBottom - stickyTop - previewHeight;

    if (scrollY >= endStick) {
      applyPreviewLayout(
        'bottom',
        {
          position: 'absolute',
          top: `${Math.max(0, containerRect.height - previewHeight)}px`,
          left: '0px',
          width: `${previewWidth}px`,
          height: 'auto'
        },
        previewHeight
      );
      return;
    }

    if (scrollY >= startStick) {
      applyPreviewLayout(
        'fixed',
        {
          position: 'fixed',
          top: `${stickyTop}px`,
          left: `${placeholderRect.left + scrollX}px`,
          width: `${previewWidth}px`,
          height: 'auto'
        },
        previewHeight
      );
      return;
    }

    applyPreviewLayout(
      'static',
      {
        position: 'relative',
        top: 'auto',
        left: 'auto',
        width: '100%',
        height: 'auto'
      },
      0
    );
  }, [activeTab, applyPreviewLayout, previewStickyTop]);

  const schedulePreviewUpdate = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (previewRafRef.current) {
      return;
    }
    previewRafRef.current = window.requestAnimationFrame(() => {
      previewRafRef.current = null;
      recomputePreviewPosition();
    });
  }, [recomputePreviewPosition]);

  useEffect(() => {
    setCampaignOptions(fetchedCampaigns || []);
  }, [fetchedCampaigns]);

  useEffect(() => {
    setStrategyOptions(availableStrategies || []);
  }, [availableStrategies]);

  useEffect(() => {
    setDnaOptions(availableDnaProfiles || []);
  }, [availableDnaProfiles]);

  useEffect(() => {
    if (companyList && companyList.length > 0) {
      const dnaCandidates = companyList.map((company) => extractDnaFromCompany(company, t)).filter(Boolean);
      if (dnaCandidates.length) {
        setDnaOptions((prev) => mergeUniqueById(prev, dnaCandidates));
      }
    } else if (currentCompany) {
      const dnaCandidate = extractDnaFromCompany(currentCompany, t);
      if (dnaCandidate) {
        setDnaOptions((prev) => mergeUniqueById(prev, [dnaCandidate]));
      }
    }
  }, [companyList, currentCompany, t]);

  useEffect(() => {
    const campaignStrategies = extractStrategiesFromCampaigns(campaignOptions, t);
    if (campaignStrategies.length) {
      setStrategyOptions((prev) => mergeUniqueById(prev, campaignStrategies));
    }
  }, [campaignOptions, t]);

  useEffect(() => {
    if (!selectedStrategyId && strategyOptions.length === 1) {
      setSelectedStrategyId(`${strategyOptions[0].id}`);
    }
  }, [selectedStrategyId, strategyOptions]);

  useEffect(() => {
    if (!selectedDnaId && dnaOptions.length === 1) {
      setSelectedDnaId(`${dnaOptions[0].id}`);
    }
  }, [selectedDnaId, dnaOptions]);

  useEffect(() => {
    setPostValidation('');
    setReferenceError('');
    setVideoReferenceError('');
    setVideoError('');
  }, [activeTab, postMode, imageMode]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const updatePreviewStickyTop = () => {
      try {
        const offsets = [32];
        const headerNode = pageHeaderRef.current;
        if (headerNode instanceof HTMLElement) {
          const headerRect = headerNode.getBoundingClientRect();
          if (Number.isFinite(headerRect.bottom)) {
            offsets.push(headerRect.bottom + 12);
          }
        }

        const shellNode = document.querySelector('.loopino-ai-shell');
        if (shellNode instanceof HTMLElement) {
          const styles = window.getComputedStyle(shellNode);
          const paddingTop = parseFloat(styles.paddingTop || '0');
          if (!Number.isNaN(paddingTop)) {
            offsets.push(paddingTop - 12);
          }
        }

        const offset = Math.max(20, Math.min(160, Math.max(...offsets)));
        if (Number.isFinite(offset)) {
          setPreviewStickyTop((prev) => (prev === offset ? prev : offset));
        }
      } catch (error) {
        console.error('[DesktopAIGeneratorPage] Unable to compute sticky offset', error);
        setPreviewStickyTop((prev) => (Number.isFinite(prev) ? prev : 36));
      }
    };

    updatePreviewStickyTop();
    window.addEventListener('resize', updatePreviewStickyTop);
    return () => {
      window.removeEventListener('resize', updatePreviewStickyTop);
    };
  }, []);

  useEffect(() => {
    if (!['image', 'video'].includes(activeTab)) {
      if (previewRafRef.current && typeof window !== 'undefined') {
        window.cancelAnimationFrame(previewRafRef.current);
        previewRafRef.current = null;
      }
      setPreviewPinMode('static');
      setPreviewDynamicStyle({});
      setPreviewPlaceholderHeight(0);
      return;
    }

    schedulePreviewUpdate();
  }, [activeTab, schedulePreviewUpdate]);

  useEffect(() => {
    if (!['image', 'video'].includes(activeTab)) {
      return undefined;
    }

    const handleScroll = () => schedulePreviewUpdate();
    const handleResize = () => schedulePreviewUpdate();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      if (previewRafRef.current) {
        window.cancelAnimationFrame(previewRafRef.current);
        previewRafRef.current = null;
      }
    };
  }, [activeTab, schedulePreviewUpdate]);

  useEffect(() => {
    if (!['image', 'video'].includes(activeTab)) {
      return;
    }
    schedulePreviewUpdate();
  }, [
    activeTab,
    schedulePreviewUpdate,
    imageMode,
    generatedImage?.url,
    imageLoading,
    useGeneratedImageAsBase,
    generatedVideo?.url,
    videoLoading
  ]);

  const resolvedAspectRatio = useMemo(
    () => (aspectRatio === 'custom' ? resolveCustomRatio(customWidth, customHeight, '4:5') : aspectRatio),
    [aspectRatio, customWidth, customHeight]
  );

  const trimmedSimplePrompt = useMemo(() => simplePromptText.trim(), [simplePromptText]);
  const trimmedVideoPrompt = useMemo(() => videoSimplePromptText.trim(), [videoSimplePromptText]);
  const hasGeneratedImage = Boolean(generatedImage?.url);
  const isProMode = imageMode === 'pro';

  const imagePreviewResetKey = useMemo(() => {
    const campaignId = selectedCampaign?.id || selectedCampaign?._id || selectedCampaign?.value || '';
    return `${activeTab || ''}-${campaignId}`;
  }, [activeTab, selectedCampaign]);

  const {
    bind: imagePreviewHoldBind,
    displayUrl: imagePreviewUrl,
    hasPrevious: hasPreviousImagePreview,
    isHoldingPrevious: isHoldingPreviousImagePreview
  } = useHoldToRevealPreviousImage(generatedImage?.url || null, { resetKey: imagePreviewResetKey });

  const resolveLabelForKey = useCallback((key) => {
    if (!key) return '';
    const normalized = key.toString().toLowerCase();
    const fallbackDefault = labelDefaults[normalized] || normalized
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

    const translationKey = normalized.replace(/[^a-z0-9]+/g, '_');
    return t(`postReview.imageEditor.promptLabels.${translationKey}`, {
      defaultValue: fallbackDefault || key
    });
  }, [labelDefaults, t]);

  const derivedPromptJson = useMemo(() => {
    if (!isProMode) return null;
    return buildPromptJsonFromBlocks(blocks, {
      normalizedRatio: resolvedAspectRatio,
      mode: 'generate'
    });
  }, [blocks, resolvedAspectRatio, isProMode]);

  const hasPromptJsonContent = useMemo(() => {
    if (!derivedPromptJson || typeof derivedPromptJson !== 'object') {
      return false;
    }
    return Object.keys(derivedPromptJson).some((key) => !RESERVED_PROMPT_JSON_KEYS.has(key) && derivedPromptJson[key]);
  }, [derivedPromptJson]);

  const buildMetadataHeader = useCallback(() => {
    const dimensions = aspectRatio === 'custom' ? `${customWidth}x${customHeight}` : aspectRatio;
    return t('imageGenerator.prompts.aspectRatio', { value: dimensions, defaultValue: `Formato: ${dimensions}` });
  }, [aspectRatio, customWidth, customHeight, t]);

  const buildPrompt = useCallback(() => {
    const joined = blocks.map((b) => b.trim()).filter(Boolean).join('\n');
    const header = buildMetadataHeader();
    return `${header}\n\n${joined}`.trim();
  }, [blocks, buildMetadataHeader]);

  const buildImagePromptWithContext = useCallback(() => {
    const basePrompt = (imageMode === 'pro' ? buildPrompt() : trimmedSimplePrompt) || '';
    const contextParts = [];
    const selectedStrategy = strategyOptions.find((item) => `${item.id}` === `${selectedStrategyId}`) || null;
    const selectedDna = dnaOptions.find((item) => `${item.id}` === `${selectedDnaId}`) || null;
    if (useStrategyForImage && selectedStrategy?.description) {
      contextParts.push(`${t('imageGenerator.settings.strategy.label', { defaultValue: 'Strategia' })}: ${selectedStrategy.description}`);
    }
    if (useDnaForImage && selectedDna?.description) {
      contextParts.push(`${t('imageGenerator.settings.dna.label', { defaultValue: 'DNA' })}: ${selectedDna.description}`);
    }
    return [basePrompt, contextParts.join('\n')].filter(Boolean).join('\n\n').trim();
  }, [
    imageMode,
    trimmedSimplePrompt,
    buildPrompt,
    strategyOptions,
    selectedStrategyId,
    dnaOptions,
    selectedDnaId,
    useStrategyForImage,
    useDnaForImage,
    t
  ]);

  const showImageAlert = useCallback((message, variant = 'warning') => {
    if (notify) {
      notify({ variant, title: message });
    }
  }, [notify]);

  const showPostAlert = useCallback((message, variant = 'warning') => {
    setPostValidation(message);
    if (notify) {
      notify({ variant, title: message });
    }
  }, [notify]);

  useEffect(() => {
    if (!generatedImage?.url && useGeneratedImageAsBase) {
      setUseGeneratedImageAsBase(false);
      return;
    }

    if (!generatedImage?.url) {
      return;
    }
  }, [generatedImage?.url, useGeneratedImageAsBase]);

  useEffect(() => {
    if (imageMode === 'pro' && useGeneratedImageAsBase) {
      setUseGeneratedImageAsBase(false);
    }
  }, [imageMode, useGeneratedImageAsBase]);

  const handleBlockChange = useCallback((idx, value) => {
    setBlocks((prev) => prev.map((b, i) => (i === idx ? value : b)));
  }, []);

  const isImageFile = (file) => {
    if (!file) return false;
    const mimeType = (file.type || '').toLowerCase();
    if (mimeType.startsWith('image/')) return true;
    const fileName = (file.name || '').toLowerCase();
    return /(jpe?g|png|gif|webp)$/.test(fileName);
  };

  const isImageTooLarge = (file) => (
    isImageFile(file) && typeof file.size === 'number' && file.size > MAX_IMAGE_UPLOAD_SIZE_BYTES
  );

  const revokePromptUrl = useCallback((url) => {
    if (url && url.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(url);
      } catch (error) {
        console.warn('[DesktopAIGeneratorPage] Unable to revoke reference url', error);
      }
    }
  }, []);

  const clearPromptImages = useCallback(() => {
    setPromptImages((prev) => {
      prev.forEach((img) => revokePromptUrl(img.url));
      return [];
    });
  }, [revokePromptUrl]);

  const removePromptImage = useCallback((imageIdOrUrl) => {
    if (!imageIdOrUrl) return;
    setPromptImages((prev) => {
      const toRemove = prev.find((img) => img?.id === imageIdOrUrl || img?.url === imageIdOrUrl);
      if (toRemove?.url) {
        revokePromptUrl(toRemove.url);
      }
      return prev.filter((img) => img?.id !== imageIdOrUrl && img?.url !== imageIdOrUrl);
    });
  }, [revokePromptUrl]);

  const addPromptImages = useCallback((payloads) => {
    const normalizedPayloads = Array.isArray(payloads) ? payloads.filter(Boolean) : payloads ? [payloads] : [];
    if (normalizedPayloads.length === 0) return;

    setPromptImages((prev) => {
      const next = [...prev];
      const remainingSlots = Math.max(0, MAX_REFERENCE_IMAGES - next.length);
      if (remainingSlots === 0) {
        normalizedPayloads.forEach((payload) => revokePromptUrl(payload?.url));
        return next;
      }

      const existingUrls = new Set(next.map((img) => img?.url).filter(Boolean));
      const added = [];
      const ignored = [];

      for (const payload of normalizedPayloads) {
        if (!payload) continue;
        if (added.length >= remainingSlots) {
          ignored.push(payload);
          continue;
        }
        if (payload.url && (existingUrls.has(payload.url) || added.some((img) => img?.url === payload.url))) {
          ignored.push(payload);
          continue;
        }
        added.push(payload);
      }

      ignored.forEach((payload) => revokePromptUrl(payload?.url));
      return [...next, ...added];
    });
  }, [revokePromptUrl]);

  useEffect(() => () => {
    promptImagesRef.current.forEach((img) => revokePromptUrl(img.url));
  }, [revokePromptUrl]);

  const handleReferenceFiles = useCallback((files) => {
    setReferenceError('');

    if (!files || files.length === 0) {
      return;
    }

    const allowedTypes = new Set(['image/png', 'image/jpeg', 'image/jpg']);
    const candidateFiles = Array.from(files).filter(Boolean);
    const referencePayloads = [];
    let nextError = '';

    candidateFiles.forEach((file) => {
      const mimeType = (file?.type || '').toLowerCase();
      const fileName = (file?.name || '').toLowerCase();
      const isAllowed = allowedTypes.has(mimeType) || /\.(png|jpe?g)$/.test(fileName);

      if (!isAllowed) {
        nextError ||= t('postReview.imageEditor.references.typeError', { defaultValue: 'Carica solo file PNG o JPG.' });
        return;
      }

      if (isImageTooLarge(file)) {
        nextError ||= t('postReview.imageEditor.references.sizeError', { maxMB: MAX_IMAGE_UPLOAD_SIZE_MB, defaultValue: 'Limite massimo 5MB.' });
        return;
      }

      const objectUrl = URL.createObjectURL(file);
      referencePayloads.push({
        id: generateMediaId(),
        name: file.name || t('desktop.upload.defaults.referenceName', { defaultValue: 'Riferimento' }),
        url: objectUrl,
        file
      });
    });

    if (referencePayloads.length > 0) {
      addPromptImages(referencePayloads);
    }

    if (nextError) {
      setReferenceError(nextError);
    }
  }, [addPromptImages, isImageTooLarge, t]);

  const handleReferenceDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingReference(false);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      handleReferenceFiles(files);
    }
  }, [handleReferenceFiles]);

  const handleReferenceDragEnter = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingReference(true);
  }, []);

  const handleReferenceDragLeave = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    setIsDraggingReference(false);
  }, []);

  const handleReferenceDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleReferenceBrowse = useCallback(() => {
    setReferenceError('');
    if (referenceInputRef.current) {
      referenceInputRef.current.click();
    }
  }, []);

  const triggerPromptImageUpload = useCallback(() => {
    setShowGallery('reference');
  }, []);

  const handleDeletePromptImage = useCallback((imageIdOrUrl) => {
    if (imageIdOrUrl) {
      removePromptImage(imageIdOrUrl);
    } else {
      clearPromptImages();
    }
    setReferenceError('');
  }, [clearPromptImages, removePromptImage]);

  const ensureUploadsAlbumId = useCallback(async () => {
    if (uploadsAlbumIdRef.current) {
      return uploadsAlbumIdRef.current;
    }

    const params = new URLSearchParams({
      page: '1',
      page_size: '50',
      include_archived: 'false'
    });

    const response = await apiClient.get(`/api/v1/media/albums?${params.toString()}`);
    const items = Array.isArray(response?.items) ? response.items : [];
    const matchingAlbum = items
      .map((entry) => entry?.album || entry?.data?.album || entry)
      .find((album) => (album?.metadata?.slug || '').toLowerCase() === 'uploads');

    const albumId = matchingAlbum?.id || matchingAlbum?._id || matchingAlbum?.album_id || matchingAlbum?.albumId;
    if (albumId) {
      uploadsAlbumIdRef.current = albumId;
      return albumId;
    }

    const payload = {
      title: t('imageGenerator.video.uploadsAlbumTitle', { defaultValue: 'Uploads' }),
      description: t('imageGenerator.video.uploadsAlbumDescription', { defaultValue: 'Upload temporanei per reference AI.' }),
      metadata: { slug: 'uploads', system: true }
    };
    const createResponse = await apiClient.post('/api/v1/media/albums', payload);
    const createdAlbum = createResponse?.album || createResponse?.data?.album || createResponse;
    const createdId = createdAlbum?.id || createdAlbum?._id || createdAlbum?.album_id || createdAlbum?.albumId;
    if (!createdId) {
      throw new Error(t('imageGenerator.video.uploadsAlbumError', { defaultValue: "Impossibile creare l'album uploads." }));
    }

    uploadsAlbumIdRef.current = createdId;
    return createdId;
  }, [apiClient, t]);

  const uploadReferenceImageToUploads = useCallback(async (file) => {
    const albumId = await ensureUploadsAlbumId();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name || 'reference');
    formData.append('media_type', 'image');
    formData.append('subtype', 'uploaded');
    formData.append('size_kb', Math.round(file.size / 1024));

    const uploadResponse = await apiClient.makeRequest(`/api/v1/media/albums/${albumId}/media/upload`, {
      method: 'POST',
      data: formData,
      isFormData: true
    });

    const mediaFile = uploadResponse?.media_file || uploadResponse?.media || uploadResponse;
    const urlCandidate = mediaFile?.public_url || mediaFile?.url;
    const resolvedUrl = toAbsoluteHttpUrl(urlCandidate) || urlCandidate;
    if (!resolvedUrl) {
      throw new Error(t('imageGenerator.video.uploadsUploadError', { defaultValue: 'Upload fallito. Riprova.' }));
    }

    return {
      id: mediaFile?.id || mediaFile?._id || generateMediaId(),
      name: mediaFile?.name || file.name || t('desktop.upload.defaults.referenceName', { defaultValue: 'Riferimento' }),
      url: resolvedUrl,
      raw: mediaFile
    };
  }, [apiClient, ensureUploadsAlbumId, t]);

  const handleVideoFrameBrowse = useCallback((frameType) => {
    setVideoReferenceError('');
    const targetRef = frameType === 'first' ? videoFirstFrameInputRef : videoLastFrameInputRef;
    if (targetRef.current) {
      targetRef.current.click();
    }
  }, []);

  const handleVideoFrameFiles = useCallback(async (files, frameType) => {
    setVideoReferenceError('');
    if (!files || files.length === 0) {
      return;
    }

    const file = Array.from(files).filter(Boolean)[0];
    if (!file) return;

    const allowedTypes = new Set(['image/png', 'image/jpeg', 'image/jpg']);
    const mimeType = (file?.type || '').toLowerCase();
    const fileName = (file?.name || '').toLowerCase();
    const isAllowed = allowedTypes.has(mimeType) || /\.(png|jpe?g)$/.test(fileName);

    if (!isAllowed) {
      setVideoReferenceError(t('postReview.imageEditor.references.typeError', { defaultValue: 'Carica solo file PNG o JPG.' }));
      return;
    }

    if (isImageTooLarge(file)) {
      setVideoReferenceError(t('postReview.imageEditor.references.sizeError', { maxMB: MAX_IMAGE_UPLOAD_SIZE_MB, defaultValue: 'Limite massimo 5MB.' }));
      return;
    }

    setVideoReferenceUploading(true);
    try {
      const uploadedItem = await uploadReferenceImageToUploads(file);
      if (frameType === 'first') {
        setVideoFirstFrame(uploadedItem);
      } else {
        setVideoLastFrame(uploadedItem);
      }
    } catch (error) {
      console.error('[DesktopAIGeneratorPage] Video frame upload failed', error);
      setVideoReferenceError(error?.message || t('imageGenerator.video.references.uploadError', { defaultValue: 'Errore nel caricamento dei riferimenti.' }));
    } finally {
      setVideoReferenceUploading(false);
    }
  }, [isImageTooLarge, t, uploadReferenceImageToUploads]);

  const handleClearVideoFrame = useCallback((frameType) => {
    if (frameType === 'first') {
      setVideoFirstFrame(null);
    } else {
      setVideoLastFrame(null);
    }
  }, []);

  const triggerVideoFrameGallery = useCallback((frameType) => {
    setShowGallery(frameType === 'first' ? 'video-first-frame' : 'video-last-frame');
  }, []);

  const handleAddVideoReferenceUrl = useCallback(() => {
    const trimmed = (videoReferenceVideoInput || '').trim();
    if (!trimmed) return;
    const normalized = toAbsoluteHttpUrl(trimmed);
    if (!normalized) {
      setVideoReferenceError(t('imageGenerator.video.references.videoUrlError', { defaultValue: 'Inserisci un URL valido.' }));
      return;
    }

    setVideoReferenceVideos((prev) => {
      if (prev.includes(normalized)) {
        return prev;
      }
      return [...prev, normalized];
    });
    setVideoReferenceVideoInput('');
    setVideoReferenceError('');
  }, [videoReferenceVideoInput, t]);

  const handleRemoveVideoReferenceUrl = useCallback((url) => {
    if (!url) return;
    setVideoReferenceVideos((prev) => prev.filter((entry) => entry !== url));
  }, []);

  const openGallery = useCallback((type) => setShowGallery(type), []);
  const closeGallery = useCallback(() => setShowGallery(null), []);

  useEffect(() => {
    if (!showGallery || typeof document === 'undefined') {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showGallery]);

  useEffect(() => {
    if (activeTab !== 'video' || videoModelsLoadedRef.current || videoModelsRequestRef.current || videoModelsAttemptedRef.current) {
      return undefined;
    }

    let isCancelled = false;
    videoModelsRequestRef.current = true;
    videoModelsAttemptedRef.current = true;
    setVideoModelsLoading(true);
    setVideoModelsError('');

    const loadVideoModels = async () => {
      try {
        const response = await apiClient.get('/api/v1/ai/video-models');
        if (isCancelled) return;
        const models = Array.isArray(response?.models) ? response.models : [];
        const normalized = models.map((model) => ({
          id: model?.model_name || model?.name,
          name: model?.model_name || model?.name,
          provider: model?.provider || model?.provider_key,
          description: model?.description || '',
          capabilities: model?.capabilities || {},
          metadata: model?.metadata || {}
        })).filter((model) => model.id);

        setVideoModels(normalized);
        if (!selectedVideoModel && normalized.length > 0) {
          setSelectedVideoModel(normalized[0].name);
        }
        videoModelsLoadedRef.current = true;
      } catch (error) {
        if (!isCancelled) {
          console.error('[DesktopAIGeneratorPage] Failed to load video models', error);
          setVideoModelsError(error?.message || t('imageGenerator.video.models.error', { defaultValue: 'Impossibile caricare i modelli video.' }));
        }
      } finally {
        if (!isCancelled) {
          setVideoModelsLoading(false);
        }
        videoModelsRequestRef.current = false;
      }
    };

    loadVideoModels();
    return () => {
      isCancelled = true;
      videoModelsRequestRef.current = false;
    };
  }, [activeTab, apiClient.get, t]);

  useEffect(() => {
    if (activeTab !== 'video') {
      videoModelsAttemptedRef.current = false;
      if (videoModelsLoading) {
        setVideoModelsLoading(false);
      }
    }
  }, [activeTab, videoModelsLoading]);

  const handleGallerySelect = useCallback((image) => {
    if (!image) {
      closeGallery();
      return;
    }
    const resolvedUrl = image.selectedUrl || image.url || image.displayUrl || image.raw?.public_url || image.raw?.url;
    if (!resolvedUrl) {
      closeGallery();
      return;
    }
    if (showGallery === 'reference') {
      addPromptImages({
        id: generateMediaId(),
        name: image.name || image.title || t('desktop.upload.defaults.referenceName', { defaultValue: 'Riferimento' }),
        url: resolvedUrl,
        raw: image
      });
      setReferenceError('');
    } else if (showGallery === 'video-first-frame' || showGallery === 'video-last-frame') {
      const payload = {
        id: generateMediaId(),
        name: image.name || image.title || t('desktop.upload.defaults.referenceName', { defaultValue: 'Riferimento' }),
        url: resolvedUrl,
        raw: image
      };
      if (showGallery === 'video-first-frame') {
        setVideoFirstFrame(payload);
      } else {
        setVideoLastFrame(payload);
      }
      setVideoReferenceError('');
    } else if (showGallery === 'manual') {
      const mediaItem = {
        id: generateMediaId(),
        type: 'url',
        url: resolvedUrl,
        previewUrl: image.displayUrl || resolvedUrl,
        name: image.name || image.title
      };
      setManualMedia((prev) => [...prev, mediaItem]);
    }
    closeGallery();
  }, [addPromptImages, closeGallery, showGallery, t]);

  const handleGenerateImage = useCallback(async () => {
    const basePrompt = imageMode === 'pro' ? buildPrompt() : trimmedSimplePrompt;
    if (!basePrompt || basePrompt.trim().length < PROMPT_MIN_LENGTH) {
      showImageAlert(t('imageGenerator.simple.promptPlaceholder', { defaultValue: 'Descrivi cosa vuoi generare' }));
      return;
    }
    if (basePrompt.length > PROMPT_MAX_LENGTH) {
      showImageAlert(t('imageGenerator.status.tooLong', { defaultValue: 'Il prompt è troppo lungo.' }));
      return;
    }

    const selectedStrategy = strategyOptions.find((item) => `${item.id}` === `${selectedStrategyId}`) || null;
    const selectedDna = dnaOptions.find((item) => `${item.id}` === `${selectedDnaId}`) || null;
    const shouldIncludeGeneratedImageAsBase = Boolean(!isProMode && useGeneratedImageAsBase && generatedImage?.url);
    const candidateReferenceUrls = [];
    if (shouldIncludeGeneratedImageAsBase && generatedImage?.url) {
      candidateReferenceUrls.push(generatedImage.url);
    }
    promptImages.slice(0, MAX_REFERENCE_IMAGES).forEach((img) => {
      if (img?.url) candidateReferenceUrls.push(img.url);
    });

    const resolvedReferenceImages = [];
    const seenReferenceUrls = new Set();
    candidateReferenceUrls
      .map(toAbsoluteHttpUrl)
      .filter(Boolean)
      .forEach((url) => {
        if (seenReferenceUrls.has(url)) return;
        seenReferenceUrls.add(url);
        resolvedReferenceImages.push(url);
      });

    const promptToSend = (buildImagePromptWithContext() || '').trim();
    const baseUserPrompt = imageMode === 'pro' ? (promptToSend || basePrompt) : trimmedSimplePrompt;

    const apiPayload = {
      prompt_text: promptToSend || basePrompt,
      user_prompt: baseUserPrompt || promptToSend || basePrompt,
      count: 1,
      ratio: resolvedAspectRatio
    };

    if (resolvedReferenceImages.length) {
      apiPayload.reference_images = resolvedReferenceImages;
    }

    if (isProMode && hasPromptJsonContent) {
      const promptJson = buildPromptJsonFromBlocks(blocks, {
        normalizedRatio: resolvedAspectRatio,
        simplePrompt: trimmedSimplePrompt || undefined,
        referenceImages: resolvedReferenceImages,
        mode: 'generate',
        baseReferenceIncluded: shouldIncludeGeneratedImageAsBase,
        includeReferenceStats: true
      });
      const promptJsonSchema = buildPromptJsonSchema(promptJson);
      const hasJsonPayload = Object.keys(promptJson).some((key) => !RESERVED_PROMPT_JSON_KEYS.has(key) && promptJson[key]);
      if (hasJsonPayload) {
        apiPayload.prompt_json = promptJson;
        if (promptJsonSchema) {
          apiPayload.prompt_json_schema = promptJsonSchema;
        }
      }
    }
    if (useStrategyForImage && selectedStrategy) {
      apiPayload.strategy_id = selectedStrategy.id;
    }
    if (useDnaForImage && selectedDna) {
      apiPayload.dna_id = selectedDna.id;
    }

    setImageLoading(true);
    try {
      if (typeof apiClient.clearError === 'function') {
        apiClient.clearError();
      }
      const response = await apiClient.post('/api/v1/ai/generate-image-new', apiPayload);
      const urlCandidates = response?.regenerated_images
        || response?.regenerated_content?.media_urls
        || (Array.isArray(response?.images) ? response.images.map((img) => img?.url || img?.local_url) : undefined)
        || (Array.isArray(response?.results?.images) ? response.results.images.map((img) => img?.url || img?.local_url) : undefined);
      const firstUrl = Array.isArray(urlCandidates) ? urlCandidates.find(Boolean) : null;
      const imageEntry = response?.images?.[0] || response?.results?.images?.[0];
      const mediaIds = response?.regenerated_content?.media_file_ids;
      const nextImage = firstUrl
        ? {
            id: (Array.isArray(mediaIds) && mediaIds[0]) || imageEntry?.id || imageEntry?._id || `generated-${Date.now()}`,
            url: firstUrl,
            name: imageEntry?.name || imageEntry?.filename || t('imageGenerator.defaults.generatedImageName', { defaultValue: 'Immagine generata' })
          }
        : null;

      if (nextImage) {
        setGeneratedImage(nextImage);
      } else {
        showImageAlert(t('imageGenerator.status.error', { defaultValue: 'Errore nella generazione, riprova.' }));
      }
    } catch (error) {
      console.error('[DesktopAIGeneratorPage] Generate failed', error);
      showImageAlert(error?.message || t('imageGenerator.status.error', { defaultValue: 'Errore nella generazione, riprova.' }), 'error');
    } finally {
      setImageLoading(false);
    }
  }, [
    imageMode,
    isProMode,
    hasPromptJsonContent,
    trimmedSimplePrompt,
    buildPrompt,
    showImageAlert,
    strategyOptions,
    selectedStrategyId,
    dnaOptions,
    selectedDnaId,
    useStrategyForImage,
    useDnaForImage,
    promptImages,
    buildImagePromptWithContext,
    blocks,
    resolvedAspectRatio,
    useGeneratedImageAsBase,
    generatedImage,
    apiClient,
    t
  ]);

  const stopVideoPolling = useCallback(() => {
    if (videoPollRef.current && typeof window !== 'undefined') {
      window.clearTimeout(videoPollRef.current);
      videoPollRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    stopVideoPolling();
  }, [stopVideoPolling]);

  const normalizeVideoResults = useCallback((job) => {
    const items = Array.isArray(job?.result?.videos) ? job.result.videos : [];
    const normalized = items.map((entry) => {
      if (!entry) return null;
      const urlCandidate = entry.public_url || entry.local_url || entry.url;
      const resolvedUrl = toAbsoluteHttpUrl(urlCandidate) || urlCandidate;
      if (!resolvedUrl) return null;
      return {
        id: entry.media_file_id || entry.filename || generateMediaId(),
        url: resolvedUrl,
        name: entry.filename || t('imageGenerator.video.defaults.generatedVideoName', { defaultValue: 'Video generato' }),
        durationSeconds: entry.duration_seconds,
        fps: entry.fps,
        width: entry.width,
        height: entry.height,
        format: entry.format,
        raw: entry
      };
    }).filter(Boolean);

    setGeneratedVideos(normalized);
    setGeneratedVideo(normalized[0] || null);
  }, [t]);

  const updateVideoJobState = useCallback((job) => {
    if (!job) return;
    setVideoJob(job);
    setVideoStatus(job.status || '');
    const progressValue = typeof job.progress === 'number' ? Math.max(0, Math.min(1, job.progress)) : null;
    setVideoProgress(progressValue);

    if (job.status === 'succeeded') {
      normalizeVideoResults(job);
      setVideoLoading(false);
      setVideoError('');
      return;
    }

    if (job.status === 'queued' || job.status === 'running') {
      setVideoError('');
      setVideoLoading(true);
      return;
    }

    if (job.status === 'failed' || job.status === 'cancelled') {
      setVideoLoading(false);
      const fallbackMessage = job.status === 'cancelled'
        ? t('imageGenerator.video.status.cancelled', { defaultValue: 'Generazione annullata.' })
        : t('imageGenerator.video.status.error', { defaultValue: 'Errore nella generazione video.' });
      const errorMessage = job?.error?.message || job?.error || fallbackMessage;
      setVideoError(errorMessage);
    }
  }, [normalizeVideoResults, t]);

  const scheduleVideoPoll = useCallback((jobId) => {
    if (!jobId || typeof window === 'undefined') return;
    stopVideoPolling();

    videoPollRef.current = window.setTimeout(async () => {
      try {
        const response = await apiClient.get(`/api/v1/ai/video-jobs/${jobId}`);
        updateVideoJobState(response);

        if (response?.status === 'queued' || response?.status === 'running') {
          scheduleVideoPoll(jobId);
        } else {
          stopVideoPolling();
        }
      } catch (error) {
        console.error('[DesktopAIGeneratorPage] Video job poll failed', error);
        setVideoError(error?.message || t('imageGenerator.video.status.error', { defaultValue: 'Errore nella generazione video.' }));
        stopVideoPolling();
        setVideoLoading(false);
      }
    }, 2500);
  }, [apiClient, stopVideoPolling, t, updateVideoJobState]);

  const handleGenerateVideo = useCallback(async () => {
    if (videoReferenceUploading) {
      showImageAlert(t('imageGenerator.video.validation.uploading', { defaultValue: 'Attendi il completamento degli upload.' }));
      return;
    }
    const basePrompt = trimmedVideoPrompt;
    if (!basePrompt || basePrompt.trim().length < VIDEO_PROMPT_MIN_LENGTH) {
      showImageAlert(t('imageGenerator.video.validation.prompt', { defaultValue: 'Descrivi il video da generare.' }));
      return;
    }
    if (basePrompt.length > VIDEO_PROMPT_MAX_LENGTH) {
      showImageAlert(t('imageGenerator.video.status.tooLong', { defaultValue: 'Il prompt è troppo lungo.' }));
      return;
    }

    const durationSeconds = parseInt(videoDuration, 10);
    if (!durationSeconds || durationSeconds < 1 || durationSeconds > 30) {
      showImageAlert(t('imageGenerator.video.validation.duration', { defaultValue: 'Durata tra 1 e 30 secondi.' }));
      return;
    }

    const fpsValue = parseInt(videoFps, 10);
    if (!fpsValue || fpsValue < 1 || fpsValue > 60) {
      showImageAlert(t('imageGenerator.video.validation.fps', { defaultValue: 'FPS tra 1 e 60.' }));
      return;
    }

    const countValue = parseInt(videoCount, 10);
    if (!countValue || countValue < 1 || countValue > 4) {
      showImageAlert(t('imageGenerator.video.validation.count', { defaultValue: 'Genera da 1 a 4 video.' }));
      return;
    }

    const ratioValue = VIDEO_RATIO_OPTIONS.includes(videoAspectRatio) ? videoAspectRatio : VIDEO_RATIO_OPTIONS[0];
    const resolutionValue = (videoResolution || VIDEO_DEFAULT_RESOLUTION).trim();

    const resolvedReferenceImages = [];
    const seenReferenceUrls = new Set();
    [videoFirstFrame?.url, videoLastFrame?.url]
      .map(toAbsoluteHttpUrl)
      .filter(Boolean)
      .forEach((url) => {
        if (seenReferenceUrls.has(url)) return;
        seenReferenceUrls.add(url);
        resolvedReferenceImages.push(url);
      });

    const resolvedReferenceVideos = [];
    const seenVideoUrls = new Set();
    videoReferenceVideos
      .map(toAbsoluteHttpUrl)
      .filter(Boolean)
      .forEach((url) => {
        if (seenVideoUrls.has(url)) return;
        seenVideoUrls.add(url);
        resolvedReferenceVideos.push(url);
      });

    const apiPayload = {
      prompt: basePrompt.trim(),
      duration_seconds: durationSeconds,
      count: countValue,
      ratio: ratioValue,
      resolution: resolutionValue,
      fps: fpsValue
    };

    if (selectedVideoModel) {
      apiPayload.model = selectedVideoModel;
    }
    if (resolvedReferenceImages.length) {
      apiPayload.reference_images = resolvedReferenceImages;
    }
    if (resolvedReferenceVideos.length) {
      apiPayload.reference_videos = resolvedReferenceVideos;
    }

    setVideoLoading(true);
    setVideoError('');
    setVideoProgress(null);
    setVideoJob(null);
    setVideoStatus('');
    stopVideoPolling();

    try {
      if (typeof apiClient.clearError === 'function') {
        apiClient.clearError();
      }
      const response = await apiClient.post('/api/v1/ai/generate-videos', apiPayload);
      const job = response?.job || response;
      if (!job?.id) {
        throw new Error(t('imageGenerator.video.status.error', { defaultValue: 'Errore nella generazione video.' }));
      }

      updateVideoJobState(job);
      if (job.status === 'queued' || job.status === 'running') {
        scheduleVideoPoll(job.id);
      }
      if (job.status === 'succeeded') {
        setVideoLoading(false);
      }
    } catch (error) {
      console.error('[DesktopAIGeneratorPage] Video generation failed', error);
      const resolvedMessage = (() => {
        if (error?.status === 402) {
          return t('imageGenerator.video.status.credits', { defaultValue: 'Crediti AI insufficienti.' });
        }
        if (error?.status === 429) {
          return t('imageGenerator.video.status.rateLimit', { defaultValue: 'Troppi tentativi. Riprova tra poco.' });
        }
        if (error?.status === 400) {
          return t('imageGenerator.video.status.invalid', { defaultValue: 'Parametri non validi per la generazione.' });
        }
        return error?.message || t('imageGenerator.video.status.error', { defaultValue: 'Errore nella generazione video.' });
      })();
      setVideoError(resolvedMessage);
      showImageAlert(resolvedMessage, 'error');
      setVideoLoading(false);
    }
  }, [
    apiClient,
    scheduleVideoPoll,
    selectedVideoModel,
    showImageAlert,
    stopVideoPolling,
    t,
    trimmedVideoPrompt,
    updateVideoJobState,
    videoAspectRatio,
    videoCount,
    videoDuration,
    videoFps,
    videoFirstFrame,
    videoLastFrame,
    videoReferenceVideos,
    videoReferenceUploading,
    videoResolution
  ]);

  const handleCancelVideoJob = useCallback(async () => {
    if (!videoJob?.id) return;
    try {
      const response = await apiClient.post(`/api/v1/ai/video-jobs/${videoJob.id}/cancel`, {});
      updateVideoJobState(response);
      stopVideoPolling();
      setVideoLoading(false);
    } catch (error) {
      console.error('[DesktopAIGeneratorPage] Video cancel failed', error);
      setVideoError(error?.message || t('imageGenerator.video.status.error', { defaultValue: 'Errore nella generazione video.' }));
    }
  }, [apiClient, stopVideoPolling, t, updateVideoJobState, videoJob?.id]);

  const handlePromptBlocksRegeneration = useCallback(async ({ prompt: modalPrompt }) => {
    const trimmedInstructions = (modalPrompt || '').trim();
    if (!trimmedInstructions) {
      showImageAlert(t('postReview.imageEditor.alerts.missingPrompt', { defaultValue: 'Inserisci delle istruzioni per rigenerare i blocchi.' }));
      return;
    }

    setIsRegeneratingBlocks(true);
    try {
      if (typeof apiClient.clearError === 'function') {
        apiClient.clearError();
      }

      const payload = { prompt_text: trimmedInstructions };
      if (hasPromptJsonContent) {
        const promptJsonSchema = buildPromptJsonSchema(derivedPromptJson);
        if (promptJsonSchema) {
          payload.prompt_json_schema = promptJsonSchema;
        }
      }

      const response = await apiClient.post('/api/v1/posts/prompt-json', payload);
      const resolvedPromptJson = response?.prompt_json || null;
      const resolvedPromptText = response?.prompt_text || '';

      if (resolvedPromptJson) {
        const regeneratedBlocks = buildBlocksFromPromptJson(resolvedPromptJson, resolveLabelForKey);
        if (regeneratedBlocks.length > 0) {
          setBlocks(regeneratedBlocks);
        }
      } else if (resolvedPromptText) {
        const regeneratedBlocks = splitPromptIntoBlocks(resolvedPromptText);
        if (regeneratedBlocks.length > 0) {
          setBlocks(regeneratedBlocks);
        }
      }
      setShowPromptBlocksModal(false);
      notify?.({ variant: 'success', title: t('postReview.imageEditor.alerts.blocksSuccess', { defaultValue: 'Blocchi rigenerati! Ricordati di salvare le modifiche.' }) });
    } catch (error) {
      console.error('[DesktopAIGeneratorPage] Prompt blocks regeneration failed', error);
      const message = error?.message || t('postReview.imageEditor.alerts.blocksError', { defaultValue: 'Impossibile rigenerare i blocchi del prompt. Riprova.' });
      notify?.({ variant: 'error', title: message });
    } finally {
      setIsRegeneratingBlocks(false);
    }
  }, [apiClient, derivedPromptJson, hasPromptJsonContent, notify, resolveLabelForKey, showImageAlert, t]);

  const resolveCampaignPlatforms = useCallback((campaign) => {
    const platforms = extractPlatformsFromCampaign(campaign);
    if (platforms && platforms.length > 0) {
      return platforms.map((platform) => `${platform}`.toLowerCase());
    }
    return ['instagram'];
  }, []);

  const extractPostIdFromResponse = useCallback((response) => {
    const pickId = (source) => {
      if (!source) return null;
      if (typeof source === 'string' || typeof source === 'number') {
        return `${source}`;
      }
      return (
        source.postId ||
        source.post_id ||
        source.id ||
        source._id ||
        source.slug ||
        source.postSlug ||
        null
      );
    };

    if (!response) return null;

    const directId = pickId(response);
    if (directId) return directId;

    const containerCandidates = [response.data, response.result];
    for (const container of containerCandidates) {
      const resolved = pickId(container);
      if (resolved) return resolved;
    }

    const postArrays = [response.posts, response.data?.posts, response.result?.posts];
    for (const postList of postArrays) {
      if (Array.isArray(postList) && postList.length > 0) {
        const resolved = pickId(postList[0]);
        if (resolved) return resolved;
      }
    }

    return null;
  }, []);

  const navigateToPostReview = useCallback((postId, source) => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams();
    if (postId) {
      params.set('postId', postId);
      params.set('targetPostId', postId);
    }
    if (source) params.set('source', source);
    const query = params.toString();
    const url = query ? `/desktop/posts?${query}` : '/desktop/posts';

    if (typeof onNavigate === 'function') {
      onNavigate(url);
      return;
    }

    window.history.pushState({}, '', url);
    window.dispatchEvent(new Event('popstate'));
  }, [onNavigate]);

  const handleGeneratePostAuto = useCallback(async () => {
    if (!selectedCampaign) {
      showPostAlert(t('imageGenerator.post.validation.campaign', { defaultValue: 'Seleziona una campagna.' }));
      return;
    }
    if (!postPrompt.trim()) {
      showPostAlert(t('imageGenerator.post.validation.prompt', { defaultValue: 'Aggiungi un prompt per il post.' }));
      return;
    }
    if (scheduleError) {
      showPostAlert(scheduleError);
      return;
    }
    if (!scheduledAt) {
      showPostAlert(t('imageGenerator.schedule.required', { defaultValue: 'Seleziona data e ora future' }));
      return;
    }

    setPostValidation('');
    setAutoPostLoading(true);
    try {
      const apiPayload = {
        use_campaign: Boolean(useStrategyForPost),
        campaign_id: selectedCampaign.id || selectedCampaign._id || selectedCampaign.value,
        use_dna: Boolean(useDnaForPost),
        user_prompt: postPrompt,
        platforms: resolveCampaignPlatforms(selectedCampaign),
        scheduled_time: scheduledAt.toISOString()
      };
      const response = await apiClient.post('/api/v1/posts/generate-via-prompt', apiPayload);
      const postId = extractPostIdFromResponse(response);
      if (postId) {
        navigateToPostReview(postId, 'auto');
      } else {
        showPostAlert(t('imageGenerator.post.validation.error', { defaultValue: 'Errore nella generazione del post.' }), 'error');
      }
    } catch (error) {
      console.error('[DesktopAIGeneratorPage] Auto post generation failed', error);
      showPostAlert(error?.message || t('imageGenerator.post.validation.error', { defaultValue: 'Errore nella generazione del post.' }), 'error');
    } finally {
      setAutoPostLoading(false);
    }
  }, [
    apiClient,
    extractPostIdFromResponse,
    navigateToPostReview,
    postPrompt,
    resolveCampaignPlatforms,
    scheduleError,
    scheduledAt,
    selectedCampaign,
    showPostAlert,
    t,
    useDnaForPost,
    useStrategyForPost
  ]);

  const manualRevokePreviewUrl = useCallback((mediaItem) => {
    if (!mediaItem || mediaItem.type !== 'file') {
      return;
    }

    const previewUrl = mediaItem.previewUrl;
    if (previewUrl && previewUrl.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(previewUrl);
      } catch (error) {
        console.warn('[DesktopAIGeneratorPage] Failed to revoke manual media URL', error);
      }
      manualMediaUrlsRef.current = manualMediaUrlsRef.current.filter((url) => url !== previewUrl);
    }
  }, []);

  useEffect(() => () => {
    const urls = manualMediaUrlsRef.current || [];
    urls.forEach((url) => {
      if (url && typeof url === 'string' && url.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(url);
        } catch (error) {
          console.warn('[DesktopAIGeneratorPage] Failed to revoke manual media URL on cleanup', error);
        }
      }
    });
    manualMediaUrlsRef.current = [];
  }, []);

  const createManualMediaItemFromFile = useCallback((file) => {
    if (!(file instanceof File)) {
      return null;
    }

    const previewUrl = URL.createObjectURL(file);
    manualMediaUrlsRef.current.push(previewUrl);

    return {
      id: generateMediaId(),
      type: 'file',
      file,
      previewUrl,
      name: file.name || t('postReview.postEdit.media.image', { defaultValue: 'Immagine' }),
      fileType: file.type,
      fileSize: file.size
    };
  }, [t]);

  const createManualMediaItemFromUrl = useCallback((url, previewUrl, options = {}) => {
    if (!url) {
      return null;
    }

    const mediaFileId = options.mediaFileId ?? options.media_file_id ?? null;
    const name = options.name || null;

    return {
      id: generateMediaId(),
      type: 'url',
      url,
      previewUrl: previewUrl || url,
      mediaFileId: mediaFileId ? `${mediaFileId}` : null,
      name: name || undefined
    };
  }, []);

  const addManualMedia = useCallback((mediaItems) => {
    if (!mediaItems || mediaItems.length === 0) return;
    setPostValidation('');
    setManualMedia((prev = []) => [...prev, ...mediaItems.filter(Boolean)]);
  }, []);

  const handleManualFiles = useCallback((files) => {
    if (!files || files.length === 0) return;
    const mediaItems = [];
    let hasOversizeImage = false;
    let hasOversizeVideo = false;

    Array.from(files).forEach((file) => {
      if (!(file instanceof File)) {
        return;
      }

      const isVideo = typeof file.type === 'string' && file.type.startsWith('video/');
      const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024;
      if (typeof file.size === 'number' && file.size > maxSize) {
        if (isVideo) {
          hasOversizeVideo = true;
        } else {
          hasOversizeImage = true;
        }
        return;
      }

      const mediaItem = createManualMediaItemFromFile(file);
      if (mediaItem) {
        mediaItems.push(mediaItem);
      }
    });

    if (mediaItems.length > 0) {
      addManualMedia(mediaItems);
    }

    if (hasOversizeImage && notify) {
      notify({
        variant: 'error',
        title: t('postReview.postEdit.media.maxSize', { defaultValue: 'Le immagini possono pesare al massimo 5MB.' })
      });
    }

    if (hasOversizeVideo && notify) {
      notify({
        variant: 'error',
        title: t('postReview.postEdit.media.videoMaxSize', { defaultValue: 'I video possono pesare al massimo 50MB.' })
      });
    }
  }, [addManualMedia, createManualMediaItemFromFile, notify, t]);

  const handleManualRemove = useCallback((index) => {
    setManualMedia((prev) => {
      if (!prev || index < 0 || index >= prev.length) return prev;
      const itemToRemove = prev[index];
      manualRevokePreviewUrl(itemToRemove);
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  }, [manualRevokePreviewUrl]);

  const handleManualReorder = useCallback((fromIndex, toIndex) => {
    setManualMedia((prev) => {
      if (!Array.isArray(prev) || fromIndex === toIndex || fromIndex == null || toIndex == null) return prev;
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= prev.length || toIndex >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      if (!moved) return prev;
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  const guessExtensionFromMime = useCallback((mimeType) => {
    if (!mimeType || typeof mimeType !== 'string') return 'jpg';
    const [, subtype = 'jpg'] = mimeType.split('/');
    const lowered = subtype.toLowerCase();
    if (lowered.includes('jpeg')) return 'jpg';
    if (lowered.includes('png')) return 'png';
    if (lowered.includes('webp')) return 'webp';
    if (lowered.includes('gif')) return 'gif';
    if (lowered.includes('mp4')) return 'mp4';
    if (lowered.includes('quicktime') || lowered.includes('mov')) return 'mov';
    if (lowered.includes('ogg')) return 'ogg';
    if (lowered.includes('webm')) return 'webm';
    return lowered.split('+')[0] || 'jpg';
  }, []);

  const buildMediaFilename = useCallback((mediaItem, index, mimeType) => {
    const baseName = (mediaItem?.name && typeof mediaItem.name === 'string' ? mediaItem.name.trim() : '') || `media-${index + 1}`;
    const extension = guessExtensionFromMime(mimeType);
    const normalizedBase = baseName.replace(/\.[^/.]+$/, '').replace(/\s+/g, '-');
    return `${normalizedBase}.${extension}`;
  }, [guessExtensionFromMime]);

  const downloadMediaAsFile = useCallback(async (mediaItem, index) => {
    const sourceUrl = mediaItem?.url || mediaItem?.previewUrl;
    if (!sourceUrl || typeof fetch !== 'function') {
      throw new Error(t('imageGenerator.post.validation.mediaDownload', { defaultValue: 'Impossibile allegare i media selezionati. Riprova.' }));
    }

    const response = await fetch(sourceUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch media: ${response.status}`);
    }
    const blob = await response.blob();
    const filename = buildMediaFilename(mediaItem, index, blob.type);
    return new File([blob], filename, { type: blob.type || 'application/octet-stream' });
  }, [buildMediaFilename, t]);

  const normalizeMediaToFile = useCallback(async (mediaItem, index) => {
    if (!mediaItem) return null;

    if (mediaItem.file instanceof File) {
      return mediaItem.file;
    }

    if (mediaItem.file instanceof Blob) {
      const mimeType = mediaItem.file.type || 'application/octet-stream';
      const filename = buildMediaFilename(mediaItem, index, mimeType);
      return new File([mediaItem.file], filename, { type: mimeType });
    }

    if (mediaItem.url || mediaItem.previewUrl) {
      return downloadMediaAsFile(mediaItem, index);
    }

    return null;
  }, [buildMediaFilename, downloadMediaAsFile]);

  const prepareManualMediaPayload = useCallback(async (postData) => {
    const mediaFileIds = [];
    let mediaIndex = 0;
    const formData = new FormData();

    for (let i = 0; i < manualMedia.length; i++) {
      const mediaItem = manualMedia[i];
      const mediaFileId = resolveMediaFileId(mediaItem) || resolveMediaFileId(mediaItem?.raw);
      if (mediaFileId) {
        mediaFileIds.push(`${mediaFileId}`);
        continue;
      }

      const normalizedFile = await normalizeMediaToFile(mediaItem, mediaIndex);
      if (!normalizedFile) {
        continue;
      }

      const filename = buildMediaFilename(mediaItem, mediaIndex, normalizedFile.type);
      formData.append(`media[${mediaIndex}]`, normalizedFile, filename);
      mediaIndex += 1;
    }

    if (mediaFileIds.length > 0) {
      postData.media_file_ids = mediaFileIds;
      mediaFileIds.forEach((id, idx) => formData.append(`media_file_ids[${idx}]`, id));
    }

    if (mediaIndex === 0 && mediaFileIds.length === 0) {
      throw new Error(t('imageGenerator.post.validation.media', { defaultValue: "Aggiungi almeno un'immagine." }));
    }

    formData.append('post_data', JSON.stringify(postData));

    return { formData, mediaFileIdsCount: mediaFileIds.length, mediaCount: mediaIndex };
  }, [buildMediaFilename, manualMedia, normalizeMediaToFile, t]);

  const handleCreatePostManual = useCallback(async () => {
    if (!selectedCampaign) {
      showPostAlert(t('imageGenerator.post.validation.campaign', { defaultValue: 'Seleziona una campagna.' }));
      return;
    }
    const descriptionText = (manualDescription || '').trim();
    if (!descriptionText) {
      showPostAlert(t('imageGenerator.post.validation.description', { defaultValue: 'Aggiungi una descrizione.' }));
      return;
    }
    if (manualMedia.length === 0) {
      showPostAlert(t('imageGenerator.post.validation.media', { defaultValue: "Aggiungi almeno un'immagine." }));
      return;
    }
    if (scheduleError) {
      showPostAlert(scheduleError);
      return;
    }
    if (!scheduledAt) {
      showPostAlert(t('imageGenerator.schedule.required', { defaultValue: 'Seleziona data e ora future' }));
      return;
    }

    setPostValidation('');
    setManualLoading(true);
    try {
      const campaignId = selectedCampaign.id || selectedCampaign._id || selectedCampaign.value;
      const postData = {
        campaign_id: campaignId,
        content: descriptionText,
        platforms: resolveCampaignPlatforms(selectedCampaign),
        scheduled_time: scheduledAt.toISOString()
      };

      const { formData } = await prepareManualMediaPayload(postData);

      const response = await apiClient.makeRequest('/api/v1/posts/create', {
        method: 'POST',
        data: formData,
        isFormData: true
      });

      const postId = extractPostIdFromResponse(response);
      if (postId) {
        navigateToPostReview(postId, 'manual');
      } else {
        showPostAlert(t('imageGenerator.post.validation.error', { defaultValue: 'Errore nella creazione del post.' }), 'error');
      }
    } catch (error) {
      console.error('[DesktopAIGeneratorPage] Manual post creation failed', error);
      showPostAlert(error?.message || t('imageGenerator.post.validation.error', { defaultValue: 'Errore nella creazione del post.' }), 'error');
    } finally {
      setManualLoading(false);
    }
  }, [
    apiClient,
    extractPostIdFromResponse,
    manualDescription,
    manualMedia,
    navigateToPostReview,
    prepareManualMediaPayload,
    resolveCampaignPlatforms,
    scheduleError,
    scheduledAt,
    selectedCampaign,
    showPostAlert,
    t
  ]);

  const generateManualImageFromPrompt = useCallback(async (promptValue) => {
    const sanitizedPrompt = (promptValue || '').trim();
    if (!sanitizedPrompt) return false;

    setManualImageGenerating(true);
    try {
      const response = await apiClient.post('/api/v1/ai/generate-images', {
        prompt: sanitizedPrompt,
        count: 1,
        ratio: '4:5',
        size: mapRatioToSize('4:5')
      });
      const imageEntry = response?.images?.[0] || response?.results?.images?.[0];
      const url = imageEntry?.url || imageEntry?.local_url;
      if (url) {
        const mediaFileId = resolveMediaFileId(imageEntry);
        const mediaItem = createManualMediaItemFromUrl(url, url, { mediaFileId }) || {
          id: generateMediaId(),
          type: 'url',
          url,
          previewUrl: url,
          mediaFileId: mediaFileId ? `${mediaFileId}` : null
        };
        addManualMedia([{
          ...mediaItem,
          name: sanitizedPrompt || t('postReview.postEdit.media.image', { defaultValue: 'Immagine' })
        }]);
        return true;
      }
    } catch (error) {
      console.error('[DesktopAIGeneratorPage] Error generating manual image from prompt', error);
      notify?.({ variant: 'error', title: error?.message || t('imageGenerator.status.error', { defaultValue: 'Errore nella generazione, riprova.' }) });
    } finally {
      setManualImageGenerating(false);
    }
    return false;
  }, [addManualMedia, apiClient, createManualMediaItemFromUrl, notify, t]);

	  const generateDescriptionFromPrompt = useCallback(async (promptValue) => {
	    const effectivePrompt = (promptValue || postPrompt || '').trim();
	    if (!effectivePrompt) {
	      showPostAlert(t('imageGenerator.post.validation.prompt', { defaultValue: 'Aggiungi un prompt per il post.' }));
	      return;
	    }
	    if (useStrategyForPost && !selectedCampaign) {
	      showPostAlert(t('imageGenerator.post.validation.campaign', { defaultValue: 'Seleziona una campagna.' }));
	      return;
	    }

	    setDescriptionLoading(true);
	    try {
	      const hasCampaign = Boolean(selectedCampaign);
	      const campaignId = hasCampaign
	        ? (selectedCampaign.id || selectedCampaign._id || selectedCampaign.value)
	        : null;
	      const shouldAttachCampaignId = Boolean(hasCampaign && (useStrategyForPost || useDnaForPost));

      const platforms = (() => {
        if (hasCampaign) {
          const resolved = extractPlatformsFromCampaign(selectedCampaign);
          if (resolved && resolved.length > 0) {
            return resolved.map((platform) => `${platform}`.toLowerCase());
          }
        }
        return ['instagram'];
	      })();

	      const apiPayload = {
	        use_campaign: Boolean(useStrategyForPost),
	        use_dna: Boolean(useDnaForPost),
	        user_prompt: effectivePrompt,
	        platform: platforms[0] || 'instagram'
	      };

	      if (shouldAttachCampaignId && campaignId) {
	        apiPayload.campaign_id = campaignId;
	      }

      const response = await apiClient.post('/api/v1/posts/generate-post-text', apiPayload);
      const generatedText = response?.content || response?.data?.content;
      const hashtags = normalizeHashtags(
        response?.metadata?.hashtags
        || response?.data?.metadata?.hashtags
        || response?.hashtags
      );

      if (!generatedText) {
        throw new Error(t('imageGenerator.post.validation.error', { defaultValue: 'Errore nella generazione del testo.' }));
      }

      const hashtagsText = hashtags.length ? `\n\n${hashtags.join(' ')}` : '';
      setManualDescription(`${generatedText}${hashtagsText}`);
      setManualAIModalOpen(false);
    } catch (error) {
      console.error('[DesktopAIGeneratorPage] Description generation failed', error);
      notify?.({ variant: 'error', title: error?.message || t('imageGenerator.post.validation.error', { defaultValue: 'Errore nella generazione del testo.' }) });
    } finally {
      setDescriptionLoading(false);
    }
  }, [
    apiClient,
    notify,
    postPrompt,
    selectedCampaign,
    showPostAlert,
    t,
    useDnaForPost,
    useStrategyForPost
  ]);

  const handleManualAIModalRegenerate = useCallback(async (data) => {
    const promptValue = data?.prompt || '';
    const type = data?.type;

    if (type === 'text') {
      await generateDescriptionFromPrompt(promptValue);
      return;
    }

    if (type === 'image') {
      const generated = await generateManualImageFromPrompt(promptValue);
      if (generated) {
        setManualAIModalOpen(false);
      }
    }
  }, [generateDescriptionFromPrompt, generateManualImageFromPrompt]);

  const formatFullSchedule = useCallback((scheduledTime) => {
    if (!scheduledTime) return t('postReview.postInfo.schedule.none', { defaultValue: 'Non programmato' });

    try {
      const date = new Date(scheduledTime);
      if (Number.isNaN(date.getTime())) {
        return t('postReview.postInfo.schedule.invalid', { defaultValue: 'Data non valida' });
      }

      return new Intl.DateTimeFormat(resolvedLocale, {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      console.error('[DesktopAIGeneratorPage] Error formatting full schedule:', error);
      return t('postReview.postInfo.schedule.invalid', { defaultValue: 'Data non valida' });
    }
  }, [resolvedLocale, t]);

  const formatDateForInput = (date) => {
    if (!date) return '';
    const iso = new Date(date).toISOString();
    return iso.slice(0, 16);
  };

  const formatTimeForInput = useCallback((dateValue) => {
    if (!dateValue) return '12:00';
    try {
      const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
      if (Number.isNaN(date.getTime())) return '12:00';
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch (error) {
      console.error('[DesktopAIGeneratorPage] Error formatting time for input:', error);
      return '12:00';
    }
  }, []);

  const isSameDay = useCallback((dateA, dateB) => {
    if (!dateA || !dateB) return false;
    return (
      dateA.getFullYear() === dateB.getFullYear() &&
      dateA.getMonth() === dateB.getMonth() &&
      dateA.getDate() === dateB.getDate()
    );
  }, []);

  const scheduleCalendarDays = useMemo(() => {
    const start = new Date(schedulePickerMonth.getFullYear(), schedulePickerMonth.getMonth(), 1);
    const leadingEmpty = (start.getDay() + 6) % 7;
    const daysInMonth = new Date(schedulePickerMonth.getFullYear(), schedulePickerMonth.getMonth() + 1, 0).getDate();
    const days = [];

    for (let i = 0; i < leadingEmpty; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(schedulePickerMonth.getFullYear(), schedulePickerMonth.getMonth(), day));
    }

    while (days.length % 7 !== 0) {
      days.push(null);
    }

    return days;
  }, [schedulePickerMonth]);

  const scheduleMonthLabel = useMemo(
    () => schedulePickerMonth.toLocaleDateString(resolvedLocale || 'it-IT', { month: 'long', year: 'numeric' }),
    [schedulePickerMonth, resolvedLocale]
  );

  const scheduleHourOptions = useMemo(
    () => Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')),
    []
  );

  const scheduleMinuteOptions = useMemo(
    () => Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0')),
    []
  );

  const syncSchedulePicker = useCallback(() => {
    const base = scheduledAt ? new Date(scheduledAt) : new Date();
    const safeDate = Number.isNaN(base.getTime()) ? new Date() : base;
    const normalized = new Date(safeDate);
    normalized.setHours(0, 0, 0, 0);
    setScheduleSelectedDate(normalized);
    setSchedulePickerMonth(normalized);
    setScheduleSelectedTime(formatTimeForInput(safeDate));
  }, [formatTimeForInput, scheduledAt]);

  useEffect(() => {
    syncSchedulePicker();
  }, [syncSchedulePicker]);

  const combineScheduleDateTime = useCallback((dateValue, timeValue) => {
    if (!dateValue || !timeValue) return null;
    const [hours, minutes] = timeValue.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      setScheduleError(t('imageGenerator.schedule.invalid', { defaultValue: 'Data non valida' }));
      return null;
    }

    const combined = new Date(dateValue);
    combined.setHours(hours, minutes, 0, 0);

    if (Number.isNaN(combined.getTime())) {
      setScheduleError(t('imageGenerator.schedule.invalid', { defaultValue: 'Data non valida' }));
      return null;
    }

    if (combined.getTime() <= Date.now()) {
      setScheduleError(t('imageGenerator.schedule.future', { defaultValue: 'Scegli una data futura.' }));
      setScheduledAt(null);
      return null;
    }

    setScheduleError('');
    setScheduledAt(combined);
    return combined;
  }, [t]);

  const handleScheduleDateSelect = useCallback((newDate) => {
    if (!newDate) return;
    const normalized = new Date(newDate);
    normalized.setHours(0, 0, 0, 0);
    if (normalized < scheduleToday) {
      return;
    }
    setScheduleSelectedDate(normalized);
    setSchedulePickerMonth(normalized);
    const timeToUse = scheduleSelectedTime || formatTimeForInput(scheduledAt);
    const resolvedTime = timeToUse || '12:00';
    setScheduleSelectedTime(resolvedTime);
    combineScheduleDateTime(normalized, resolvedTime);
  }, [combineScheduleDateTime, formatTimeForInput, scheduleSelectedTime, scheduleToday, scheduledAt]);

  const handleScheduleTimeInputChange = useCallback((timeValue) => {
    if (!timeValue) return;
    const finalTime = timeValue.slice(0, 5);
    setScheduleSelectedTime(finalTime);
    const dateToUse = scheduleSelectedDate || new Date();
    combineScheduleDateTime(dateToUse, finalTime);
    setScheduleTimePickerOpen(false);
  }, [combineScheduleDateTime, scheduleSelectedDate]);

  const handleScheduleHourSelect = useCallback((hour) => {
    const [, minutes = '00'] = (scheduleSelectedTime || '12:00').split(':');
    handleScheduleTimeInputChange(`${hour}:${minutes}`);
  }, [handleScheduleTimeInputChange, scheduleSelectedTime]);

  const handleScheduleMinuteSelect = useCallback((minute) => {
    const [hours = '12'] = (scheduleSelectedTime || '12:00').split(':');
    handleScheduleTimeInputChange(`${hours}:${minute}`);
  }, [handleScheduleTimeInputChange, scheduleSelectedTime]);

  const goToPreviousScheduleMonth = useCallback(() => {
    setSchedulePickerMonth((prev) => {
      const next = new Date(prev);
      next.setMonth(prev.getMonth() - 1);
      return next;
    });
  }, []);

  const goToNextScheduleMonth = useCallback(() => {
    setSchedulePickerMonth((prev) => {
      const next = new Date(prev);
      next.setMonth(prev.getMonth() + 1);
      return next;
    });
  }, []);

  const openSchedulePicker = useCallback(() => {
    syncSchedulePicker();
    setScheduleTimePickerOpen(false);
    setSchedulePickerOpen(true);
  }, [syncSchedulePicker]);

  const closeSchedulePicker = useCallback(() => {
    setSchedulePickerOpen(false);
    setScheduleTimePickerOpen(false);
  }, []);

  const openScheduleTimePicker = useCallback(() => {
    if (scheduleTimeInputRef.current) {
      scheduleTimeInputRef.current.focus();
    }
    setScheduleTimePickerOpen(true);
  }, []);

  const handleScheduleBoxKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openSchedulePicker();
    }
  }, [openSchedulePicker]);

  useEffect(() => {
    if (!isScheduleTimePickerOpen) return undefined;
    const handleClickOutside = (event) => {
      if (scheduleTimePanelRef.current?.contains(event.target) || scheduleTimeInputRef.current?.contains(event.target)) {
        return;
      }
      setScheduleTimePickerOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isScheduleTimePickerOpen]);

  const handleCampaignSelect = useCallback((campaign) => {
    const normalizedId = normalizeParamId(campaign?.id ?? campaign?._id ?? campaign?.value);
    const normalizedCampaign = normalizedId ? { ...campaign, id: normalizedId } : campaign;
    setSelectedCampaign(normalizedCampaign);
    setPostValidation('');
    setReferenceError('');

    const derivedStrategyId = deriveStrategyIdFromCampaign(campaign);
    if (derivedStrategyId) {
      setSelectedStrategyId(`${derivedStrategyId}`);
    }
  }, []);

  useEffect(() => {
    const initialSelection = initialSelectionRef.current;
    if (!initialSelection || initialSelection.applied) return;

    const {
      tab,
      campaignId,
      strategyId,
      dnaId,
      useCompanyDna,
      useDna,
      useStrategy
    } = initialSelection;

    if (campaignId && campaignOptions.length === 0) {
      return;
    }

    if (useCompanyDna && companyLoading && !currentCompany) {
      return;
    }

    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }

    let resolvedCampaign = null;
    if (campaignId) {
      resolvedCampaign = campaignOptions.find((campaign) => (
        normalizeParamId(campaign?.id ?? campaign?._id ?? campaign?.value) === `${campaignId}`
      )) || null;

      if (resolvedCampaign) {
        handleCampaignSelect(resolvedCampaign);
      }
    }

    const resolvedStrategyId = normalizeParamId(strategyId)
      || normalizeParamId(resolvedCampaign ? deriveStrategyIdFromCampaign(resolvedCampaign) : null);
    if (resolvedStrategyId) {
      setSelectedStrategyId(`${resolvedStrategyId}`);
    }

    let resolvedDnaId = normalizeParamId(dnaId);
    if (!resolvedDnaId && useCompanyDna) {
      const companyDna = extractDnaFromCompany(currentCompany, t);
      resolvedDnaId = normalizeParamId(resolveDnaId(companyDna));
    }
    if (resolvedDnaId) {
      setSelectedDnaId(`${resolvedDnaId}`);
    }

    if (showPostGenerationSettings && (useStrategy || resolvedStrategyId)) {
      setUseStrategyForPost(true);
    }
    if (showPostGenerationSettings && (useDna || resolvedDnaId || useCompanyDna)) {
      setUseDnaForPost(true);
    }

    initialSelection.applied = true;
  }, [
    activeTab,
    campaignOptions,
    campaignsLoading,
    companyLoading,
    currentCompany,
    handleCampaignSelect,
    showPostGenerationSettings,
    t
  ]);

  const renderHeader = () => (
    <header className="loopino-desktop-post-review-header" ref={pageHeaderRef}>
      <div>
        <p className="loopino-desktop-post-review-eyebrow">
          {t('desktop.common.eyebrow', { defaultValue: 'Loopino desktop' })}
        </p>
        <h1>
          {t('desktop.nav.links.aiGenerator', { defaultValue: 'Crea post' })}
        </h1>
        <p className="loopino-desktop-post-review-summary">
          {t('imageGenerator.desktop.subtitle', { defaultValue: 'Genera immagini, testi e post pronti per la pubblicazione.' })}
        </p>
      </div>
    </header>
  );

  const renderStrategySelectors = (variant) => {
    const selectedStrategy = strategyOptions.find((item) => `${item.id}` === `${selectedStrategyId}`) || null;
    const selectedDna = dnaOptions.find((item) => `${item.id}` === `${selectedDnaId}`) || null;
    const useDna = variant === 'image' ? useDnaForImage : useDnaForPost;
    const useStrategy = variant === 'image' ? useStrategyForImage : useStrategyForPost;
    const handleDnaToggle = variant === 'image' ? setUseDnaForImage : setUseDnaForPost;
    const handleStrategyToggle = variant === 'image' ? setUseStrategyForImage : setUseStrategyForPost;

    return (
      <div className="loopino-ai-card compact">
        <div className="loopino-ai-card-header">
          <div>
            <p className="loopino-eyebrow">{t('imageGenerator.settings.eyebrow', { defaultValue: 'Contesto' })}</p>
            <h3 className="loopino-ai-card-title">{t('imageGenerator.settings.title', { defaultValue: 'DNA e strategia' })}</h3>
          </div>
          {variant === 'image' && (
            <div className="loopino-badge subtle">
              {t('imageGenerator.settings.scopeImage', { defaultValue: 'Per immagini' })}
            </div>
          )}
        </div>
        <div className="loopino-ai-toggle-row">
          <div>
            <p className="loopino-ai-toggle-label">{t('imageGenerator.settings.dna.label', { defaultValue: 'Usa DNA' })}</p>
            <p className="loopino-ai-toggle-sub">{selectedDna?.label || t('imageGenerator.settings.dnaFallback', { defaultValue: 'Seleziona un DNA' })}</p>
          </div>
          <DualOptionToggle
            active={useDna}
            onChange={(next) => handleDnaToggle(next)}
            leftLabel={t('imageGenerator.toggle.off', { defaultValue: 'Off' })}
            rightLabel={t('imageGenerator.toggle.on', { defaultValue: 'On' })}
            ariaLabel={t('imageGenerator.settings.dnaToggleAria', { defaultValue: 'Attiva o disattiva il DNA per la generazione' })}
          />
        </div>
        <div className="loopino-ai-toggle-row">
          <div>
            <p className="loopino-ai-toggle-label">{t('imageGenerator.settings.strategy.label', { defaultValue: 'Usa strategia' })}</p>
            <p className="loopino-ai-toggle-sub">{selectedStrategy?.label || t('imageGenerator.settings.strategyFallback', { defaultValue: 'Seleziona una strategia' })}</p>
          </div>
          <DualOptionToggle
            active={useStrategy}
            onChange={(next) => handleStrategyToggle(next)}
            leftLabel={t('imageGenerator.toggle.off', { defaultValue: 'Off' })}
            rightLabel={t('imageGenerator.toggle.on', { defaultValue: 'On' })}
            ariaLabel={t('imageGenerator.settings.strategyToggleAria', { defaultValue: 'Attiva o disattiva la strategia per la generazione' })}
          />
        </div>
        {variant === 'image' && (
          <div className="loopino-ai-select-row">
            <select
              className="loopino-select"
              value={selectedStrategyId || ''}
              onChange={(e) => setSelectedStrategyId(e.target.value || null)}
            >
              <option value="">{t('imageGenerator.settings.strategyFallback', { defaultValue: 'Nessuna strategia' })}</option>
              {strategyOptions.map((strategy) => (
                <option key={strategy.id} value={strategy.id}>{strategy.label}</option>
              ))}
            </select>
            <select
              className="loopino-select"
              value={selectedDnaId || ''}
              onChange={(e) => setSelectedDnaId(e.target.value || null)}
            >
              <option value="">{t('imageGenerator.settings.dnaFallback', { defaultValue: 'Nessun DNA' })}</option>
              {dnaOptions.map((dna) => (
                <option key={dna.id} value={dna.id}>{dna.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>
    );
  };

  const renderImageControls = () => {
    const referenceImages = Array.isArray(promptImages) ? promptImages.slice(0, MAX_REFERENCE_IMAGES) : [];
    const currentImageName = generatedImage?.name || t('imageGenerator.defaults.generatedImageName', { defaultValue: 'Immagine generata' });
    const displayImageUrl = imagePreviewUrl || null;
    const previewClassName = [
      'loopino-ai-image-preview-sticky',
      previewPinMode === 'fixed' ? 'is-fixed' : '',
      previewPinMode === 'bottom' ? 'is-bottom' : ''
    ].filter(Boolean).join(' ');
    const mergedPreviewStyle = {
      ...(previewPanelStyle || {}),
      ...(previewDynamicStyle || {})
    };
    const imageModelSelector = (
      <div className="loopino-video-model-selector">
        <div className="loopino-field">
          <label className="loopino-field-label">{t('imageGenerator.image.settings.model', { defaultValue: 'Modello' })}</label>
          <DNADropdown
            value={selectedImageModel}
            options={imageModelOptions}
            onChange={setSelectedImageModel}
            disabled={imageLoading}
            ariaLabel={t('imageGenerator.image.settings.model', { defaultValue: 'Modello' })}
          />
        </div>
      </div>
    );

    if (isImageInitialLoading) {
      return (
        <div className="loopino-ai-image-mode" aria-busy="true" aria-label={t('common.loading', { defaultValue: 'Caricamento' })}>
          <div className="loopino-image-editor-grid" ref={imageEditorGridRef}>
            <div className="loopino-image-editor-col">
              <div className="loopino-ai-image-preview-wrap">
                <div
                  className="loopino-ai-image-preview-placeholder"
                  ref={imagePreviewPlaceholderRef}
                  aria-hidden
                  style={previewPlaceholderStyle}
                />
                <div
                  className={previewClassName}
                  style={mergedPreviewStyle}
                  ref={imagePreviewPanelRef}
                >
                  <div className="loopino-card loopino-image-preview-card">
                    <div className="loopino-card-header" aria-hidden="true">
                      <div style={{ width: '100%' }}>
                        <Skeleton width="34%" height={12} shimmer />
                        <Skeleton width="58%" height={22} shimmer style={{ marginTop: 10 }} />
                      </div>
                    </div>

                    <div className="loopino-image-preview-frame" aria-hidden="true">
                      <Skeleton variant="rounded" width="100%" height={360} shimmer />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="loopino-image-editor-col">
              <div className="loopino-glass-panel loopino-image-editor-panel">
                <div className="loopino-mode-toggle" aria-hidden="true">
                  <button type="button" className="loopino-mode-option" disabled tabIndex={-1}>
                    <Skeleton width="60%" height={14} shimmer />
                  </button>
                  <button type="button" className="loopino-mode-option" disabled tabIndex={-1}>
                    <Skeleton width="60%" height={14} shimmer />
                  </button>
                </div>

                {imageMode === 'pro' ? (
                  <>
                    <div className="loopino-image-editor-section" aria-hidden="true">
                      <div className="loopino-section-heading">
                        <div style={{ width: '100%' }}>
                          <Skeleton width="28%" height={12} shimmer />
                          <Skeleton width="42%" height={20} shimmer style={{ marginTop: 10 }} />
                        </div>
                      </div>
                      <div className="loopino-blocks-grid">
                        {Array.from({ length: 5 }).map((_, idx) => (
                          <div className="loopino-block-card" key={`skeleton-block-${idx}`}>
                            <div className="loopino-block-header">
                              <Skeleton variant="circle" width={26} height={26} shimmer />
                              <Skeleton width="38%" height={14} shimmer />
                            </div>
                            <Skeleton variant="rounded" width="100%" height={44} shimmer />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="loopino-image-editor-section" aria-hidden="true">
                      <div className="loopino-section-heading">
                        <div style={{ width: '100%' }}>
                          <Skeleton width="30%" height={12} shimmer />
                          <Skeleton width="48%" height={20} shimmer style={{ marginTop: 10 }} />
                        </div>
                      </div>
                      <Skeleton variant="rounded" width="100%" height={44} shimmer />
                    </div>
                  </>
                ) : (
                  <div className="loopino-image-editor-section" aria-hidden="true">
                    <div className="loopino-section-heading">
                      <div style={{ width: '100%' }}>
                        <Skeleton width="22%" height={12} shimmer />
                        <Skeleton width="56%" height={20} shimmer style={{ marginTop: 10 }} />
                      </div>
                    </div>
                    <Skeleton variant="rounded" width="100%" height={150} shimmer />
                  </div>
                )}

                <div className="loopino-reference-section" aria-hidden="true">
                  <div className="loopino-section-heading">
                    <div style={{ width: '100%' }}>
                      <Skeleton width="28%" height={12} shimmer />
                      <Skeleton width="66%" height={18} shimmer style={{ marginTop: 10 }} />
                    </div>
                  </div>

                  <div className="loopino-reference-dropzone">
                    <div className="loopino-reference-drop-content">
                      <Skeleton variant="circle" width={52} height={52} shimmer style={{ marginBottom: 6 }} />
                      <Skeleton width="62%" height={16} shimmer />
                      <Skeleton width="44%" height={14} shimmer style={{ marginTop: 6 }} />
                      <div className="loopino-reference-drop-actions">
                        <Skeleton variant="rounded" width={160} height={40} shimmer />
                        <Skeleton variant="rounded" width={160} height={40} shimmer />
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: 10 }}>
                    <Skeleton width="92%" height={14} shimmer />
                    <Skeleton width="76%" height={14} shimmer style={{ marginTop: 8 }} />
                  </div>
                </div>

                <div className="loopino-image-editor-footer" aria-hidden="true">
                  {imageMode === 'pro' && <Skeleton variant="rounded" width={170} height={44} shimmer />}
                  <Skeleton variant="rounded" width={220} height={44} shimmer />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const referenceSection = (
      <div className="loopino-reference-section">
        <div className="loopino-section-heading">
          <div>
            <p className="loopino-section-eyebrow">{t('postReview.imageEditor.references.title')}</p>
            <h3>{t('postReview.imageEditor.references.description', { defaultValue: "Aggiungi immagini guida per l'AI" })}</h3>
          </div>
        </div>

        <div
          className={`loopino-reference-dropzone ${isDraggingReference ? 'dragging' : ''}`}
          onDragEnter={handleReferenceDragEnter}
          onDragOver={handleReferenceDragOver}
          onDragLeave={handleReferenceDragLeave}
          onDrop={handleReferenceDrop}
        >
          <input
            ref={referenceInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg"
            multiple
            hidden
            onChange={(event) => {
              handleReferenceFiles(event.target.files);
              event.target.value = '';
            }}
          />
          <div className="loopino-reference-drop-content">
            <div className="loopino-reference-drop-icon">
              <img src={iconUpload} alt="" aria-hidden="true" className="loopino-icon-invert" />
            </div>
            <p className="loopino-reference-drop-title">
              {t('postReview.imageEditor.references.dropTitle', { defaultValue: 'Trascina qui le immagini' })}
            </p>
            <p className="loopino-reference-drop-sub">
              PNG o JPG · {t('postReview.imageEditor.references.maxSize', { size: MAX_IMAGE_UPLOAD_SIZE_MB, defaultValue: 'Max 5MB' })}
            </p>
            <div className="loopino-reference-drop-actions">
              <button type="button" className="loopino-button ghost" onClick={handleReferenceBrowse}>
                <img src={iconUpload} alt="" aria-hidden="true" className="loopino-button-icon loopino-icon-invert" />
                {t('postReview.imageEditor.references.upload')}
              </button>
              <button type="button" className="loopino-button ghost" onClick={triggerPromptImageUpload}>
                <img src={iconImage} alt="" aria-hidden="true" className="loopino-button-icon" />
                {t('postReview.imageEditor.references.gallery')}
              </button>
            </div>
          </div>
        </div>

        {referenceError && (
          <div className="loopino-reference-error" role="alert">
            {referenceError}
          </div>
        )}

        {referenceImages.length > 0 ? (
          referenceImages.map((promptImage) => (
            <div className="loopino-reference-file" key={promptImage.id || promptImage.url}>
              <div className="loopino-reference-file-main">
                <div className="loopino-reference-thumb">
                  <img
                    src={promptImage.url}
                    alt={promptImage.name}
                    onError={(e) => {
                      e.target.src = placeholderImage;
                    }}
                  />
                </div>
                <div className="loopino-reference-info">
                  <p className="loopino-reference-title">{promptImage.name}</p>
                  <p className="loopino-reference-sub">
                    {promptImage.file?.size ? formatBytes(promptImage.file.size) : t('postReview.imageEditor.references.title')}
                  </p>
                </div>
              </div>
              <button
                className="loopino-icon-button"
                onClick={() => handleDeletePromptImage(promptImage.id || promptImage.url)}
                aria-label={t('postReview.imageEditor.references.deleteAria')}
                type="button"
              >
                <img src={iconDelete} alt={t('postReview.imageEditor.references.deleteAlt')} />
              </button>
            </div>
          ))
        ) : (
          <p className="loopino-reference-helper">
            {t('postReview.imageEditor.references.helper', { defaultValue: 'Trascina una singola immagine di riferimento o scegli dal dispositivo.' })}
          </p>
        )}
      </div>
    );

    return (
      <div className="loopino-ai-image-mode">
        <div className="loopino-image-editor-grid" ref={imageEditorGridRef}>
          <div className="loopino-image-editor-col">
            <div className="loopino-ai-image-preview-wrap">
              <div
                className="loopino-ai-image-preview-placeholder"
                ref={imagePreviewPlaceholderRef}
                aria-hidden
                style={previewPlaceholderStyle}
              />
              <div
                className={previewClassName}
                style={mergedPreviewStyle}
                ref={imagePreviewPanelRef}
              >
                <div className="loopino-card loopino-image-preview-card">
                  <div className="loopino-card-header">
                    <div>
                      <p className="loopino-card-eyebrow">{t('postReview.imageEditor.currentImage', { defaultValue: 'Immagine attuale' })}</p>
                      <h3>{currentImageName}</h3>
                    </div>
                  </div>

                  <div className="loopino-image-preview-frame" {...imagePreviewHoldBind}>
                    {displayImageUrl ? (
                      <>
                        <img
                          src={displayImageUrl}
                          alt={isHoldingPreviousImagePreview ? `${currentImageName} (precedente)` : currentImageName}
                          title={hasPreviousImagePreview ? t('postReview.imageEditor.preview.holdForPrevious', { defaultValue: "Tieni premuto per vedere l'immagine precedente" }) : undefined}
                          draggable={false}
                          onError={(e) => {
                            e.target.src = placeholderImage;
                          }}
                        />
                        {imageLoading && (
                          <AILoadingOverlay text={t('postReview.imageEditor.overlay.generating', { defaultValue: 'Generazione in corso' })} />
                        )}
                      </>
                    ) : (
                      <div className="loopino-image-placeholder">
                        <img src={iconImage} alt={t('postReview.imageEditor.references.emptyAlt')} />
                        <p>{t('postReview.imageEditor.currentImagePlaceholder', { defaultValue: "Genera l'immagine" })}</p>
                      </div>
                    )}
                  </div>

                  <div className="generation-settings-toggle loopino-image-base-toggle">
                    <div className="generation-toggle-row generation-toggle-row-stacked">
                      <div className="generation-toggle-text">
                        <h3 className="generation-toggle-title">
                          {t('postReview.imageEditor.baseToggle.title', { defaultValue: 'Immagine base' })}
                        </h3>
                        <p className="generation-toggle-subtitle">
                          {t('postReview.imageEditor.baseToggle.helper', { defaultValue: "Usa l'immagine attuale come base per le variazioni." })}
                        </p>
                      </div>
                      <DualOptionToggle
                        active={useGeneratedImageAsBase}
                        onChange={(next) => setUseGeneratedImageAsBase(next)}
                        leftLabel={t('imageGenerator.toggle.off', { defaultValue: 'Off' })}
                        rightLabel={t('imageGenerator.toggle.on', { defaultValue: 'On' })}
                        disabled={!hasGeneratedImage || isProMode}
                        ariaLabel={t('postReview.imageEditor.baseToggle.aria', { defaultValue: "Attiva o disattiva l'uso dell'immagine generata come base" })}
                        className="ai-image-base-toggle"
                      />
                    </div>
                  {hasGeneratedImage && isProMode && (
                    <p className="loopino-reference-helper">
                      {t('postReview.imageEditor.baseToggle.proDisabled', { defaultValue: 'Disponibile solo in modalità Fast.' })}
                    </p>
                  )}
                </div>
                {imageModelSelector}
              </div>
            </div>
          </div>
          </div>

          <div className="loopino-image-editor-col">
            <div className="loopino-glass-panel loopino-image-editor-panel">
              <div className="loopino-mode-toggle">
                <button
                  className={`loopino-mode-option ${imageMode === 'fast' ? 'active' : ''}`}
                  onClick={() => setImageMode('fast')}
                  type="button"
                >
                  {t('postReview.imageEditor.mode.fast', { defaultValue: 'Fast' })}
                </button>
                <button
                  className={`loopino-mode-option ${imageMode === 'pro' ? 'active' : ''}`}
                  onClick={() => setImageMode('pro')}
                  type="button"
                >
                  {t('postReview.imageEditor.mode.proLabel', { defaultValue: 'Pro' })}
                </button>
              </div>

              {imageMode === 'fast' && (
                <div className="loopino-image-editor-section">
                  <div className="loopino-section-heading">
                    <div>
                      <p className="loopino-section-eyebrow">{t('postReview.imageEditor.fast.title', { defaultValue: 'Fast' })}</p>
                      <h3>{t('postReview.imageEditor.fast.subtitle', { defaultValue: 'Imput per la generazione' })}</h3>
                    </div>
                  </div>
                  <textarea
                    className="loopino-textarea"
                    value={simplePromptText}
                    onChange={(e) => setSimplePromptText(e.target.value)}
                    placeholder={t('postReview.imageEditor.fast.placeholder', { defaultValue: 'Es: Un cane con le gambe di coccodrillo, stile fumetto anni 90...' })}
                    rows={6}
                  />
                  {referenceSection}
                </div>
              )}

              {imageMode === 'pro' && (
                <>
                  <div className="loopino-image-editor-section">
                    <div className="loopino-section-heading">
                      <div>
                        <p className="loopino-section-eyebrow">
                          {t('desktop.prompt.structuredInput', { defaultValue: 'Input strutturato' })}
                        </p>
                        <h3>{t('postReview.imageEditor.pro.title', { defaultValue: 'Blocchi' })}</h3>
                      </div>
                    </div>

                    <div className="loopino-blocks-grid">
                      {blocks.map((block, idx) => {
                        const rawBlock = typeof block === 'string' ? block : '';
                        const match = PROMPT_BLOCK_REGEX.exec(rawBlock);
                        const label = match
                          ? match[1].trim()
                          : t('postReview.imageEditor.blocks.defaultLabel', { defaultValue: 'Blocco' });
                        const content = match ? match[3] : rawBlock;
                        const placeholder = buildBlockPlaceholder(label, idx);
                        return (
                          <div className="loopino-block-card" key={`block-${idx}`}>
                            <div className="loopino-block-header">
                              <div className="loopino-block-index">{idx + 1}</div>
                              <div className="loopino-block-title">{label}</div>
                            </div>
                            <AutoGrowTextarea
                              className="loopino-textarea loopino-block-textarea"
                              value={content}
                              onChange={(e) => handleBlockChange(idx, `${label}: ${e.target.value}`)}
                              placeholder={placeholder}
                              rows={1}
                              wrap="soft"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="loopino-image-editor-section">
                    <div className="loopino-section-heading">
                      <div>
                        <p className="loopino-section-eyebrow">{t('postReview.imageEditor.options.ratiosTitle')}</p>
                        <h3>{t('postReview.imageEditor.options.ratiosSubtitle', { defaultValue: 'Dimensioni immagine' })}</h3>
                      </div>
                    </div>
                    <div className="loopino-segmented-control">
                      {[{ key: '1:1', label: '1:1' }, { key: '16:9', label: '16:9' }, { key: '9:16', label: '9:16' }, { key: '4:3', label: '4:3' }, { key: '3:4', label: '3:4' }, { key: '4:5', label: '4:5' }, { key: 'custom', label: t('postReview.imageEditor.options.customLabel') }].map(({ key, label }) => (
                        <button
                          key={key}
                          className={`loopino-segment ${aspectRatio === key ? 'active' : ''}`}
                          onClick={() => setAspectRatio(key)}
                          type="button"
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    {aspectRatio === 'custom' && (
                      <div className="loopino-custom-dimensions">
                        <input
                          type="number"
                          className="loopino-dimension-input"
                          placeholder={t('postReview.imageEditor.options.widthPlaceholder')}
                          value={customWidth}
                          onChange={(e) => setCustomWidth(e.target.value)}
                          min="256"
                          max="2048"
                        />
                        <span className="loopino-dimension-separator">×</span>
                        <input
                          type="number"
                          className="loopino-dimension-input"
                          placeholder={t('postReview.imageEditor.options.heightPlaceholder')}
                          value={customHeight}
                          onChange={(e) => setCustomHeight(e.target.value)}
                          min="256"
                          max="2048"
                        />
                      </div>
                    )}
                  </div>

                  {referenceSection}
                </>
              )}

	              <div className="loopino-image-editor-footer">
	                {imageMode === 'pro' && (
	                  isRegeneratingBlocks ? (
	                    <EstimatedProgressBar
	                      className="loopino-estimated-progress-primary"
	                      label={t('postReview.imageEditor.footer.generating', { defaultValue: 'Generazione...' })}
	                      percent={promptBlocksPercent}
	                      ariaLabel={t('postReview.imageEditor.footer.generating', { defaultValue: 'Generazione blocchi prompt' })}
	                    />
	                  ) : (
	                    <button
	                      type="button"
	                      className="loopino-button ghost"
	                      onClick={() => setShowPromptBlocksModal(true)}
	                      disabled={imageLoading}
	                    >
	                      <AIIcon />
	                      {t('postReview.imageEditor.footer.regenerateBlocks', { defaultValue: 'Rigenera blocchi' })}
	                    </button>
	                  )
	                )}
	                {imageLoading ? (
	                  <EstimatedProgressBar
	                    className="loopino-estimated-progress-primary loopino-estimated-progress-full"
	                    label={t('postReview.imageEditor.footer.generating', { defaultValue: 'Generazione...' })}
	                    percent={imageGenerationPercent}
	                    ariaLabel={t('postReview.imageEditor.footer.generating', { defaultValue: 'Generazione immagine' })}
	                  />
	                ) : (
	                  <button
	                    className="loopino-button primary"
	                    onClick={handleGenerateImage}
	                    disabled={imageLoading}
	                    type="button"
	                  >
	                    <img src={iconImage} alt={t('postReview.imageEditor.footer.iconAlt')} className="loopino-button-icon" />
	                    {t('postReview.imageEditor.footer.generate', { defaultValue: 'Genera immagine' })}
	                  </button>
	                )}
	              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderVideoControls = () => {
    const currentVideoName = generatedVideo?.name || t('imageGenerator.video.defaults.generatedVideoName', { defaultValue: 'Video generato' });
    const displayVideoUrl = generatedVideo?.url || null;
    const previewClassName = [
      'loopino-ai-image-preview-sticky',
      previewPinMode === 'fixed' ? 'is-fixed' : '',
      previewPinMode === 'bottom' ? 'is-bottom' : ''
    ].filter(Boolean).join(' ');
    const mergedPreviewStyle = {
      ...(previewPanelStyle || {}),
      ...(previewDynamicStyle || {})
    };
    const isVideoJobActive = videoStatus === 'queued' || videoStatus === 'running';
    const isVideoBusy = videoLoading || videoReferenceUploading;
    const isResolutionOptionDisabled = (option) => (
      supportedVideoResolutions && !supportedVideoResolutions.has(normalizeVideoCapabilityValue(option))
    );
    const isFpsOptionDisabled = (value) => (
      supportedVideoFps && !supportedVideoFps.has(value)
    );

    if (isVideoInitialLoading) {
      return (
        <div className="loopino-ai-image-mode loopino-ai-video-mode" aria-busy="true" aria-label={t('common.loading', { defaultValue: 'Caricamento' })}>
          <div className="loopino-image-editor-grid" ref={imageEditorGridRef}>
            <div className="loopino-image-editor-col">
              <div className="loopino-ai-image-preview-wrap">
                <div
                  className="loopino-ai-image-preview-placeholder"
                  ref={imagePreviewPlaceholderRef}
                  aria-hidden
                  style={previewPlaceholderStyle}
                />
                <div
                  className={previewClassName}
                  style={mergedPreviewStyle}
                  ref={imagePreviewPanelRef}
                >
                  <div className="loopino-card loopino-image-preview-card">
                    <div className="loopino-card-header" aria-hidden="true">
                      <div style={{ width: '100%' }}>
                        <Skeleton width="34%" height={12} shimmer />
                        <Skeleton width="58%" height={22} shimmer style={{ marginTop: 10 }} />
                      </div>
                    </div>

                    <div className="loopino-image-preview-frame" aria-hidden="true">
                      <Skeleton variant="rounded" width="100%" height={360} shimmer />
                    </div>

                    <div className="generation-settings-toggle loopino-image-base-toggle" aria-hidden="true">
                      <div className="generation-toggle-row">
                        <Skeleton width="64%" height={16} shimmer />
                        <Skeleton variant="rounded" width={96} height={34} shimmer />
                      </div>
                      <Skeleton width="78%" height={14} shimmer style={{ marginTop: 12 }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="loopino-image-editor-col">
              <div className="loopino-glass-panel loopino-image-editor-panel">
                <div className="loopino-mode-toggle" aria-hidden="true">
                  <button type="button" className="loopino-mode-option" disabled tabIndex={-1}>
                    <Skeleton width="60%" height={14} shimmer />
                  </button>
                  <button type="button" className="loopino-mode-option" disabled tabIndex={-1}>
                    <Skeleton width="60%" height={14} shimmer />
                  </button>
                </div>

                <div className="loopino-image-editor-section" aria-hidden="true">
                  <div className="loopino-section-heading">
                    <div style={{ width: '100%' }}>
                      <Skeleton width="22%" height={12} shimmer />
                      <Skeleton width="56%" height={20} shimmer style={{ marginTop: 10 }} />
                    </div>
                  </div>
                  <Skeleton variant="rounded" width="100%" height={150} shimmer />
                </div>

                <div className="loopino-image-editor-section" aria-hidden="true">
                  <div className="loopino-section-heading">
                    <div style={{ width: '100%' }}>
                      <Skeleton width="28%" height={12} shimmer />
                      <Skeleton width="66%" height={18} shimmer style={{ marginTop: 10 }} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 'var(--loopino-space-sm)' }}>
                    <Skeleton variant="rounded" width="100%" height={44} shimmer />
                    <Skeleton variant="rounded" width="100%" height={44} shimmer />
                    <Skeleton variant="rounded" width="100%" height={44} shimmer />
                    <Skeleton variant="rounded" width="100%" height={44} shimmer />
                  </div>
                </div>

                <div className="loopino-reference-section" aria-hidden="true">
                  <div className="loopino-section-heading">
                    <div style={{ width: '100%' }}>
                      <Skeleton width="28%" height={12} shimmer />
                      <Skeleton width="66%" height={18} shimmer style={{ marginTop: 10 }} />
                    </div>
                  </div>

                  <div className="loopino-reference-dropzone">
                    <div className="loopino-reference-drop-content">
                      <Skeleton variant="circle" width={52} height={52} shimmer style={{ marginBottom: 6 }} />
                      <Skeleton width="62%" height={16} shimmer />
                      <Skeleton width="44%" height={14} shimmer style={{ marginTop: 6 }} />
                      <div className="loopino-reference-drop-actions">
                        <Skeleton variant="rounded" width={160} height={40} shimmer />
                        <Skeleton variant="rounded" width={160} height={40} shimmer />
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: 10 }}>
                    <Skeleton width="92%" height={14} shimmer />
                    <Skeleton width="76%" height={14} shimmer style={{ marginTop: 8 }} />
                  </div>
                </div>

                <div className="loopino-image-editor-footer" aria-hidden="true">
                  <Skeleton variant="rounded" width={220} height={44} shimmer />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const videoModelSelector = (
      <div className="loopino-video-model-selector">
        <div className="loopino-field">
          <label className="loopino-field-label">{t('imageGenerator.video.settings.model', { defaultValue: 'Modello' })}</label>
          <DNADropdown
            value={selectedVideoModel}
            options={videoModelOptions}
            onChange={setSelectedVideoModel}
            placeholder={t('imageGenerator.video.settings.modelAuto', { defaultValue: 'Auto' })}
            disabled={videoModelsLoading || videoLoading}
            ariaLabel={t('imageGenerator.video.settings.model', { defaultValue: 'Modello' })}
          />
        </div>
        {videoModelsError && <div className="loopino-alert danger">{videoModelsError}</div>}
      </div>
    );

    const videoSettingsSection = (
      <div className="loopino-image-editor-section">
        <div className="loopino-section-heading">
          <div>
            <p className="loopino-section-eyebrow">{t('imageGenerator.video.settings.eyebrow', { defaultValue: 'Impostazioni video' })}</p>
            <h3>{t('imageGenerator.video.settings.title', { defaultValue: 'Parametri di generazione' })}</h3>
          </div>
        </div>
        <div className="loopino-grid two loopino-video-settings-grid">
          <div className="loopino-field">
            <label className="loopino-field-label">{t('imageGenerator.video.settings.resolution', { defaultValue: 'Risoluzione' })}</label>
            <div className="loopino-segmented-control">
              {VIDEO_RESOLUTION_OPTIONS.map((resolution) => {
                const isDisabled = isResolutionOptionDisabled(resolution);
                return (
                  <button
                    key={resolution}
                    className={`loopino-segment ${videoResolution === resolution ? 'active' : ''}`}
                    onClick={() => setVideoResolution(resolution)}
                    type="button"
                    disabled={videoLoading || isDisabled}
                  >
                    {resolution}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="loopino-field">
            <label className="loopino-field-label">{t('imageGenerator.video.settings.fps', { defaultValue: 'FPS' })}</label>
            <div className="loopino-segmented-control">
              {VIDEO_FPS_OPTIONS.map((option) => {
                const isDisabled = isFpsOptionDisabled(option.value);
                return (
                  <button
                    key={option.value}
                    className={`loopino-segment ${normalizeVideoFpsValue(videoFps) === option.value ? 'active' : ''}`}
                    onClick={() => setVideoFps(option.value)}
                    type="button"
                    disabled={videoLoading || isDisabled}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="loopino-grid two loopino-video-settings-grid">
          <div className="loopino-field">
            <label className="loopino-field-label">{t('imageGenerator.video.settings.duration', { defaultValue: 'Durata (s)' })}</label>
            <DNADropdown
              value={videoDuration}
              options={videoDurationOptions}
              onChange={setVideoDuration}
              disabled={videoLoading}
              ariaLabel={t('imageGenerator.video.settings.duration', { defaultValue: 'Durata (s)' })}
            />
          </div>
          <div className="loopino-field">
            <label className="loopino-field-label">{t('imageGenerator.video.settings.count', { defaultValue: 'Quantità' })}</label>
            <DNADropdown
              value={videoCount}
              options={videoCountOptions}
              onChange={setVideoCount}
              disabled={videoLoading}
              ariaLabel={t('imageGenerator.video.settings.count', { defaultValue: 'Quantità' })}
            />
          </div>
        </div>
        <div className="loopino-section-heading" style={{ marginTop: 'var(--loopino-space-lg)' }}>
          <div>
            <p className="loopino-section-eyebrow">{t('imageGenerator.video.settings.ratioEyebrow', { defaultValue: 'Formato' })}</p>
            <h3>{t('imageGenerator.video.settings.ratioTitle', { defaultValue: 'Aspect ratio' })}</h3>
          </div>
        </div>
        <div className="loopino-segmented-control">
          {VIDEO_RATIO_OPTIONS.map((ratio) => (
            <button
              key={ratio}
              className={`loopino-segment ${videoAspectRatio === ratio ? 'active' : ''}`}
              onClick={() => setVideoAspectRatio(ratio)}
              type="button"
              disabled={videoLoading}
            >
              {ratio}
            </button>
          ))}
        </div>
      </div>
    );

    const videoFrameSlots = [
      {
        key: 'first',
        label: t('imageGenerator.video.frames.first', { defaultValue: 'Primo frame (opzionale)' }),
        frame: videoFirstFrame
      },
      {
        key: 'last',
        label: t('imageGenerator.video.frames.last', { defaultValue: 'Ultimo frame (opzionale)' }),
        frame: videoLastFrame
      }
    ];

    const renderVideoFrameCard = ({ key, label, frame }) => {
      const hasFrame = Boolean(frame?.url);
      const subLabel = hasFrame
        ? (frame?.name || t('imageGenerator.video.frames.selected', { defaultValue: 'Frame selezionato' }))
        : t('imageGenerator.video.frames.optional', { defaultValue: 'Opzionale' });

      return (
        <div className="loopino-reference-item loopino-video-frame-card" key={key}>
          {hasFrame ? (
            <img
              src={frame.url}
              alt={frame.name || label}
              onError={(e) => {
                e.target.src = placeholderImage;
              }}
            />
          ) : (
            <div className="loopino-reference-empty">
              <img src={iconImage} alt="" aria-hidden="true" />
              <p>{t('imageGenerator.video.frames.empty', { defaultValue: 'Nessun frame selezionato' })}</p>
            </div>
          )}
          <div className="loopino-reference-meta loopino-video-frame-meta">
            <div>
              <p className="loopino-reference-title">{label}</p>
              <p className="loopino-reference-sub">{subLabel}</p>
            </div>
            <div className="loopino-inline-actions loopino-video-frame-actions">
              <input
                ref={key === 'first' ? videoFirstFrameInputRef : videoLastFrameInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                hidden
                onChange={(event) => {
                  handleVideoFrameFiles(event.target.files, key);
                  event.target.value = '';
                }}
              />
              <button
                type="button"
                className="loopino-button ghost"
                onClick={() => handleVideoFrameBrowse(key)}
                disabled={videoReferenceUploading || videoLoading}
              >
                <img src={iconUpload} alt="" aria-hidden="true" className="loopino-button-icon loopino-icon-invert" />
                {t('postReview.imageEditor.references.upload')}
              </button>
              <button
                type="button"
                className="loopino-button ghost"
                onClick={() => triggerVideoFrameGallery(key)}
                disabled={videoReferenceUploading || videoLoading}
              >
                <img src={iconImage} alt="" aria-hidden="true" className="loopino-button-icon" />
                {t('postReview.imageEditor.references.gallery')}
              </button>
              {hasFrame && (
                <button
                  className="loopino-icon-button"
                  onClick={() => handleClearVideoFrame(key)}
                  aria-label={t('postReview.imageEditor.references.deleteAria')}
                  type="button"
                  disabled={videoReferenceUploading || videoLoading}
                >
                  <img src={iconDelete} alt={t('postReview.imageEditor.references.deleteAlt')} />
                </button>
              )}
            </div>
          </div>
        </div>
      );
    };

    const referenceSection = (
      <div className="loopino-reference-section">
        <div className="loopino-section-heading">
          <div>
            <p className="loopino-section-eyebrow">
              {t('imageGenerator.video.references.eyebrow', { defaultValue: 'Media di riferimento' })}
            </p>
            <h3>{t('imageGenerator.video.references.description', { defaultValue: "Aggiungi immagini guida per l'AI" })}</h3>
          </div>
        </div>

        <div className="loopino-video-frame-grid">
          {videoFrameSlots.map((slot) => renderVideoFrameCard(slot))}
        </div>

        {videoReferenceUploading && (
          <p className="loopino-reference-helper">
            {t('imageGenerator.video.references.uploading', { defaultValue: 'Caricamento in corso...' })}
          </p>
        )}

        {videoReferenceError && (
          <div className="loopino-reference-error" role="alert">
            {videoReferenceError}
          </div>
        )}

        <div className="loopino-field" style={{ marginTop: 'var(--loopino-space-md)' }}>
          <label className="loopino-field-label">
            {t('imageGenerator.video.references.videoLabel', { defaultValue: 'Video di riferimento (URL)' })}
          </label>
          <div className="loopino-inline-fields">
            <input
              className="loopino-input"
              value={videoReferenceVideoInput}
              onChange={(e) => setVideoReferenceVideoInput(e.target.value)}
              placeholder={t('imageGenerator.video.references.videoDisabledPlaceholder', { defaultValue: 'Non disponibile con il modello corrente.' })}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleAddVideoReferenceUrl();
                }
              }}
              disabled
            />
            <button
              type="button"
              className="loopino-button ghost"
              onClick={handleAddVideoReferenceUrl}
              disabled
            >
              {t('imageGenerator.video.references.addVideo', { defaultValue: 'Aggiungi' })}
            </button>
          </div>
          {videoReferenceVideos.length > 0 ? (
            videoReferenceVideos.map((url) => (
              <div className="loopino-reference-file" key={url}>
                <div className="loopino-reference-file-main">
                  <div className="loopino-reference-thumb">
                    <img src={iconVideo} alt="" className="loopino-reference-icon" />
                  </div>
                  <div className="loopino-reference-info">
                    <p className="loopino-reference-title">{t('imageGenerator.video.references.videoItem', { defaultValue: 'Video riferimento' })}</p>
                    <p className="loopino-reference-sub">{url}</p>
                  </div>
                </div>
                <button
                  className="loopino-icon-button"
                  onClick={() => handleRemoveVideoReferenceUrl(url)}
                  aria-label={t('postReview.imageEditor.references.deleteAria')}
                  type="button"
                >
                  <img src={iconDelete} alt={t('postReview.imageEditor.references.deleteAlt')} />
                </button>
              </div>
            ))
          ) : (
            <p className="loopino-reference-helper">
              {t('imageGenerator.video.references.videoDisabledHelper', { defaultValue: 'Non disponibile con il modello corrente (Veo 3.1).' })}
            </p>
          )}
        </div>
      </div>
    );

    return (
      <div className="loopino-ai-image-mode loopino-ai-video-mode">
        <div className="loopino-image-editor-grid" ref={imageEditorGridRef}>
          <div className="loopino-image-editor-col">
            <div className="loopino-ai-image-preview-wrap">
              <div
                className="loopino-ai-image-preview-placeholder"
                ref={imagePreviewPlaceholderRef}
                aria-hidden
                style={previewPlaceholderStyle}
              />
              <div
                className={previewClassName}
                style={mergedPreviewStyle}
                ref={imagePreviewPanelRef}
              >
                <div className="loopino-card loopino-image-preview-card">
                  <div className="loopino-card-header">
                    <div>
                      <p className="loopino-card-eyebrow">{t('imageGenerator.video.currentVideo', { defaultValue: 'Video attuale' })}</p>
                      <h3>{currentVideoName}</h3>
                    </div>
                  </div>

                  <div className="loopino-image-preview-frame">
                    {displayVideoUrl ? (
                      <>
                        <video src={displayVideoUrl} controls preload="metadata" />
                        {videoLoading && (
                          <AILoadingOverlay text={t('imageGenerator.video.overlay.generating', { defaultValue: 'Generazione in corso' })} />
                        )}
                      </>
                    ) : (
                      <div className="loopino-image-placeholder">
                        <img src={iconVideo} alt={t('imageGenerator.video.references.emptyAlt', { defaultValue: 'Video' })} />
                        <p>{t('imageGenerator.video.currentVideoPlaceholder', { defaultValue: 'Genera il video' })}</p>
                      </div>
                    )}
                  </div>

                  {videoModelSelector}
                </div>
              </div>
            </div>
          </div>

          <div className="loopino-image-editor-col">
            <div className="loopino-glass-panel loopino-image-editor-panel">
              <div className="loopino-image-editor-section">
                <div className="loopino-section-heading">
                  <div>
                    <p className="loopino-section-eyebrow">{t('postReview.imageEditor.fast.title', { defaultValue: 'Fast' })}</p>
                    <h3>{t('imageGenerator.video.fast.subtitle', { defaultValue: 'Genera in pochi click usando prompt e riferimenti.' })}</h3>
                  </div>
                </div>
                <textarea
                  className="loopino-textarea"
                  value={videoSimplePromptText}
                  onChange={(e) => setVideoSimplePromptText(e.target.value)}
                  placeholder={t('imageGenerator.video.fast.placeholder', { defaultValue: 'Es: Una skyline futuristica al tramonto con droni in volo...' })}
                  rows={6}
                />
              </div>

              {videoSettingsSection}
              {referenceSection}

              <div className="loopino-image-editor-footer">
                {videoLoading ? (
                  <div className="loopino-inline-actions">
                    <EstimatedProgressBar
                      className="loopino-estimated-progress-primary loopino-estimated-progress-full"
                      label={t('imageGenerator.video.footer.generating', { defaultValue: 'Generazione...' })}
                      percent={videoDisplayPercent}
                      ariaLabel={t('imageGenerator.video.footer.generating', { defaultValue: 'Generazione video' })}
                    />
                    {isVideoJobActive && (
                      <button
                        type="button"
                        className="loopino-button ghost"
                        onClick={handleCancelVideoJob}
                      >
                        {t('imageGenerator.video.footer.cancel', { defaultValue: 'Annulla' })}
                      </button>
                    )}
                  </div>
                ) : (
                  <button
                    className="loopino-button primary"
                    onClick={handleGenerateVideo}
                    disabled={isVideoBusy}
                    type="button"
                  >
                    <img src={iconVideo} alt={t('imageGenerator.video.footer.iconAlt', { defaultValue: 'Video' })} className="loopino-button-icon" />
                    {t('imageGenerator.video.footer.generate', { defaultValue: 'Genera video' })}
                  </button>
                )}
              </div>
              {videoError && <div className="loopino-alert danger">{videoError}</div>}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPostAuto = () => (
    <div className="loopino-ai-post-section">
      <div className="loopino-field">
        <label className="loopino-field-label">{t('imageGenerator.post.auto.promptTitle', { defaultValue: 'Comando per generare il post' })}</label>
        <textarea
          className="loopino-textarea"
          rows={6}
          value={postPrompt}
          onChange={(e) => setPostPrompt(e.target.value)}
          placeholder={t('imageGenerator.post.auto.promptPlaceholder', { defaultValue: 'Scrivi cosa vuoi generare per il post...' })}
        />
	      </div>
	      <div className="loopino-inline-actions loopino-post-auto-cta">
	        {autoPostLoading ? (
	          <EstimatedProgressBar
	            className="loopino-estimated-progress-primary"
	            label={t('imageGenerator.post.auto.loading', { defaultValue: 'Generazione post...' })}
	            percent={postGenerationPercent}
	            ariaLabel={t('imageGenerator.post.auto.loading', { defaultValue: 'Generazione post' })}
	          />
	        ) : (
	          <button
	            type="button"
	            className="loopino-button primary"
	            onClick={handleGeneratePostAuto}
	          >
	            {t('imageGenerator.post.auto.cta', { defaultValue: 'Genera post' })}
	          </button>
	        )}
	      </div>
	    </div>
	  );

  const renderPostManual = () => (
    <div className="loopino-ai-post-section">
      <DesktopPostMediaManager
        mediaArray={manualMedia}
        isUiDisabled={manualLoading || manualImageGenerating}
        isGenerating={manualImageGenerating}
        onUploadFiles={handleManualFiles}
        onOpenGenerate={() => { setManualAiModalType('image'); setManualAIModalOpen(true); }}
        onOpenGallery={() => openGallery('manual')}
        onDelete={handleManualRemove}
        onReorder={handleManualReorder}
      />

      <div className="post-edit-section">
        <div className="post-edit-section-header">
          <div>
            <h2 className="post-edit-section-title">{t('postReview.postEdit.description.title', { defaultValue: 'Descrizione post' })}</h2>
            <p className="post-edit-section-description">
              {t('postReview.postEdit.description.subtitle', { defaultValue: 'Puoi scrivere o generare con AI.' })}
            </p>
          </div>
          <button
            type="button"
            className="loopino-post-edit-icon-button"
            onClick={() => { setManualAiModalType('text'); setManualAIModalOpen(true); }}
            disabled={descriptionLoading}
            aria-label={t('imageGenerator.post.manual.generateText', { defaultValue: 'Genera testo' })}
          >
            <img src={iconAi} alt="" aria-hidden="true" />
          </button>
        </div>

        <textarea
          className="post-edit-textarea"
          rows={8}
          value={manualDescription}
          onChange={(e) => setManualDescription(e.target.value)}
          placeholder={t('postReview.postEdit.description.placeholder', { defaultValue: 'Scrivi qui la descrizione del post...' })}
        />

        <div className="loopino-inline-actions loopino-post-auto-cta">
          <button
            type="button"
            className="loopino-button primary"
            onClick={handleCreatePostManual}
            disabled={manualLoading}
          >
            {manualLoading ? t('imageGenerator.status.generating', { defaultValue: 'Creazione...' }) : t('imageGenerator.post.manual.cta', { defaultValue: 'Crea post' })}
          </button>
        </div>
        {postValidation && <div className="loopino-alert danger">{postValidation}</div>}
      </div>
    </div>
  );

	  const renderPostSection = () => (
	    <div className="loopino-ai-card">
	      <div className="loopino-ai-post-grid">
	        {isPostInitialLoading ? (
	          <>
	            <div className="loopino-ai-post-panel">
                <div aria-busy="true" aria-label={t('common.loading', { defaultValue: 'Caricamento' })}>
                  <div aria-hidden="true">
                    <Skeleton width="22%" height={12} shimmer />
                    <Skeleton width="54%" height={22} shimmer style={{ marginTop: 10 }} />

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 'var(--loopino-space-sm)', marginTop: 'var(--loopino-space-md)' }}>
                      <Skeleton variant="rounded" width="100%" height={44} shimmer />
                      <Skeleton variant="rounded" width="100%" height={44} shimmer />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--loopino-space-sm)', marginTop: 'var(--loopino-space-md)' }}>
                      <Skeleton variant="rounded" width="100%" height={72} shimmer />
                      <Skeleton variant="rounded" width="100%" height={72} shimmer />
                      <Skeleton variant="rounded" width="100%" height={72} shimmer />
                    </div>
                  </div>

                  {showPostGenerationSettings && (
                    <div className="loopino-ai-card compact" style={{ marginTop: 'var(--loopino-space-lg)' }} aria-hidden="true">
                      <div className="loopino-ai-card-header">
                        <div style={{ width: '100%' }}>
                          <Skeleton width="24%" height={12} shimmer />
                          <Skeleton width="48%" height={20} shimmer style={{ marginTop: 10 }} />
                        </div>
                      </div>
                      <div className="loopino-ai-toggle-row">
                        <div style={{ width: '100%' }}>
                          <Skeleton width="32%" height={14} shimmer />
                          <Skeleton width="56%" height={12} shimmer style={{ marginTop: 8 }} />
                        </div>
                        <Skeleton variant="rounded" width={96} height={34} shimmer />
                      </div>
                      <div className="loopino-ai-toggle-row">
                        <div style={{ width: '100%' }}>
                          <Skeleton width="38%" height={14} shimmer />
                          <Skeleton width="60%" height={12} shimmer style={{ marginTop: 8 }} />
                        </div>
                        <Skeleton variant="rounded" width={96} height={34} shimmer />
                      </div>
                    </div>
                  )}

                  <div className="loopino-field loopino-ai-schedule-field" style={{ marginTop: 'var(--loopino-space-lg)' }} aria-hidden="true">
                    <div className="loopino-desktop-post-datetime-box">
                      <div className="loopino-desktop-meta-label">
                        <Skeleton width="34%" height={12} shimmer />
                      </div>
                      <div className="loopino-desktop-meta-value">
                        <Skeleton variant="circle" width={18} height={18} shimmer />
                        <Skeleton width="64%" height={18} shimmer />
                      </div>
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <Skeleton width="90%" height={12} shimmer />
                    </div>
                  </div>
                </div>
	            </div>
	            <div className="loopino-ai-post-panel">
                <div aria-busy="true" aria-label={t('common.loading', { defaultValue: 'Caricamento' })}>
                  <div className="loopino-mode-toggle loopino-ai-post-toggle" aria-hidden="true">
                    <button type="button" className="loopino-mode-option" disabled tabIndex={-1}>
                      <Skeleton width="66%" height={14} shimmer />
                    </button>
                    <button type="button" className="loopino-mode-option" disabled tabIndex={-1}>
                      <Skeleton width="66%" height={14} shimmer />
                    </button>
                  </div>

                  {postMode === 'manual' ? (
                    <div className="loopino-ai-post-section" style={{ marginTop: 'var(--loopino-space-lg)' }} aria-hidden="true">
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 'var(--loopino-space-sm)' }}>
                        {Array.from({ length: 4 }).map((_, idx) => (
                          <Skeleton key={`skeleton-media-${idx}`} variant="rounded" width="100%" height={110} shimmer />
                        ))}
                      </div>

                      <div className="post-edit-section" style={{ marginTop: 'var(--loopino-space-lg)' }}>
                        <div className="post-edit-section-header">
                          <div style={{ width: '100%' }}>
                            <Skeleton width="46%" height={20} shimmer />
                            <Skeleton width="78%" height={14} shimmer style={{ marginTop: 10 }} />
                          </div>
                          <Skeleton variant="circle" width={40} height={40} shimmer />
                        </div>
                        <Skeleton variant="rounded" width="100%" height={160} shimmer />
                        <div className="loopino-inline-actions loopino-post-auto-cta">
                          <Skeleton variant="rounded" width={200} height={44} shimmer />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="loopino-ai-post-section" style={{ marginTop: 'var(--loopino-space-lg)' }} aria-hidden="true">
                      <div className="loopino-field">
                        <Skeleton width="52%" height={14} shimmer />
                        <Skeleton variant="rounded" width="100%" height={150} shimmer />
                      </div>
                      <div className="loopino-inline-actions loopino-post-auto-cta">
                        <Skeleton variant="rounded" width={180} height={44} shimmer />
                      </div>
                    </div>
                  )}
                </div>
	            </div>
	          </>
	        ) : (
	          <>
	            <div className="loopino-ai-post-panel">
	              <DesktopCampaignSelector
	                campaigns={campaignOptions}
	                selectionMode="single"
	                activeCampaign={selectedCampaign}
	                onCampaignSelect={handleCampaignSelect}
	                requireConfirmation={false}
	                className="loopino-ai-campaign-selector"
	                eyebrow={t('imageGenerator.post.campaignEyebrow', { defaultValue: 'Campagna' })}
	                title={t('imageGenerator.post.campaignTitle', { defaultValue: 'Scegli la campagna' })}
	              />

	              {showPostGenerationSettings ? renderStrategySelectors('post') : null}

	              <div className="loopino-field loopino-ai-schedule-field">
	                <div
	                  className="loopino-desktop-post-datetime-box"
	                  role="button"
	                  tabIndex={0}
	                  onClick={openSchedulePicker}
	                  onKeyDown={handleScheduleBoxKeyDown}
	                >
	                  <div className="loopino-desktop-meta-label">
	                    {t('postReview.postInfo.schedule.label', { defaultValue: 'Data e ora' })}
	                  </div>
	                  <div className="loopino-desktop-meta-value">
	                    <img src={iconCalendar} alt="" aria-hidden="true" />
	                    <div className="loopino-desktop-datetime-copy">
	                      <span className="loopino-desktop-datetime-main">
	                        {scheduledAt
	                          ? formatFullSchedule(scheduledAt)
	                          : t('imageGenerator.schedule.chooseDate', { defaultValue: 'scegli una data' })}
	                      </span>
	                    </div>
	                  </div>
	                </div>
	                <p className="loopino-helper loopino-inline-helper">
	                  <img src={iconClock} alt="" aria-hidden="true" />
	                  {t('imageGenerator.schedule.helper', { defaultValue: 'L’orario usa il tuo fuso locale. Imposta data futura per programmare.' })}
	                </p>
	                {scheduleError && <div className="loopino-alert danger">{scheduleError}</div>}
	              </div>
	            </div>

	            <div className="loopino-ai-post-panel">
	              <div className="loopino-mode-toggle loopino-ai-post-toggle">
	                <button
	                  type="button"
	                  className={`loopino-mode-option ${postMode === 'auto' ? 'active' : ''}`}
	                  onClick={() => setPostMode('auto')}
	                >
	                  {t('imageGenerator.post.auto.label', { defaultValue: 'Automatica' })}
	                </button>
	                <button
	                  type="button"
	                  className={`loopino-mode-option ${postMode === 'manual' ? 'active' : ''}`}
	                  onClick={() => setPostMode('manual')}
	                >
	                  {t('imageGenerator.post.manual.label', { defaultValue: 'Manuale' })}
	                </button>
	              </div>

	              {postMode === 'auto' ? renderPostAuto() : renderPostManual()}
	            </div>
	          </>
	        )}
	      </div>
	    </div>
	  );

  const isHydratingCampaignOptions = useMemo(() => (
    !campaignsLoading &&
    Array.isArray(fetchedCampaigns) &&
    fetchedCampaigns.length > 0 &&
    campaignOptions.length === 0
  ), [campaignOptions.length, campaignsLoading, fetchedCampaigns]);

  const hasPotentialDnaSource = useMemo(() => {
    const candidates = Array.isArray(companyList) && companyList.length > 0
      ? companyList
      : currentCompany
        ? [currentCompany]
        : [];

    return candidates.some((company) => Boolean(
      company?.generated_dna ||
      company?.dna ||
      company?.ai_analysis?.company_identity
    ));
  }, [companyList, currentCompany]);

  const isHydratingDnaOptions = useMemo(() => (
    !companyLoading && hasPotentialDnaSource && dnaOptions.length === 0
  ), [companyLoading, dnaOptions.length, hasPotentialDnaSource]);

  const hasPotentialStrategies = useMemo(() => (
    Array.isArray(fetchedCampaigns) && fetchedCampaigns.some((campaign) => Boolean(campaign?.strategy))
  ), [fetchedCampaigns]);

  const isHydratingStrategyOptions = useMemo(() => (
    !campaignsLoading && hasPotentialStrategies && strategyOptions.length === 0
  ), [campaignsLoading, hasPotentialStrategies, strategyOptions.length]);

  const isImageInitialLoading = campaignsLoading || companyLoading;
  const isPostInitialLoading = isImageInitialLoading || isHydratingCampaignOptions || isHydratingDnaOptions || isHydratingStrategyOptions;
  const isVideoInitialLoading = videoModelsLoading;
  const imageGenerationPercent = useEstimatedProgressPercent(imageLoading, 30000, 99);
  const postGenerationPercent = useEstimatedProgressPercent(autoPostLoading, 60000, 99);
  const promptBlocksPercent = useEstimatedProgressPercent(isRegeneratingBlocks, 30000, 99);
  const videoGenerationPercent = useEstimatedProgressPercent(videoLoading, 60000, 99);
  const videoDisplayPercent = useMemo(() => (
    typeof videoProgress === 'number' ? Math.round(videoProgress * 100) : videoGenerationPercent
  ), [videoGenerationPercent, videoProgress]);
  const portalTarget = typeof document !== 'undefined' ? document.body : null;

  return (
    <>
      <div className="loopino-desktop-shell loopino-ai-shell">
        <div className="loopino-ai-grid">
          {renderHeader()}
          <div className="loopino-ai-main-toggle">
            <TripleOptionToggle
              value={activeTab}
              labels={[
                { value: 'post', label: t('imageGenerator.tabs.post', { defaultValue: 'Post' }) },
                { value: 'image', label: t('imageGenerator.tabs.image', { defaultValue: 'Immagini' }) },
                { value: 'video', label: t('imageGenerator.tabs.video', { defaultValue: 'Video' }) }
              ]}
              onChange={setActiveTab}
              ariaLabel={t('imageGenerator.tabs.toggleAria', { defaultValue: 'Seleziona tra post, immagini e video' })}
              className="triple-option-toggle-wide"
            />
          </div>

          {activeTab === 'post'
            ? renderPostSection()
            : activeTab === 'video'
              ? renderVideoControls()
              : renderImageControls()}
    </div>
  </div>

      {isSchedulePickerOpen && portalTarget && createPortal(
        <div
          className="mobile-date-picker-overlay"
          onClick={closeSchedulePicker}
          aria-hidden="true"
        >
          <div
            className="mobile-date-picker-popover"
            role="dialog"
            aria-label={t('postReview.postEdit.time.label', { defaultValue: 'Data e ora' })}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mobile-date-picker comp-503">
              <div className="mobile-datetime-label">
                <ScheduleCalendarIcon size={18} />
                <span>{t('postReview.postEdit.time.label', { defaultValue: 'Data e ora' })}</span>
              </div>
              <div className="mobile-date-picker-header">
                <button
                  type="button"
                  className="date-picker-nav"
                  onClick={goToPreviousScheduleMonth}
                  aria-label={t('postReview.postEdit.time.prevMonth', { defaultValue: 'Mese precedente' })}
                >
                  <BackIcon size={18} />
                </button>
                <div className="mobile-date-picker-month">{scheduleMonthLabel}</div>
                <button
                  type="button"
                  className="date-picker-nav next"
                  onClick={goToNextScheduleMonth}
                  aria-label={t('postReview.postEdit.time.nextMonth', { defaultValue: 'Mese successivo' })}
                >
                  <BackIcon size={18} />
                </button>
              </div>

              <div className="mobile-date-picker-body single-column">
                <div className="mobile-date-picker-calendar">
                  <div className="mobile-date-picker-weekdays">
                    {scheduleWeekdayLabels.map((label) => (
                      <span key={label} className="mobile-date-picker-weekday">
                        {label}
                      </span>
                    ))}
                  </div>
                  <div className="mobile-date-picker-grid">
                    {scheduleCalendarDays.map((day, index) => {
                      if (!day) {
                        return <span key={`empty-${index}`} className="mobile-date-picker-day empty"></span>;
                      }
                      const isSelected = isSameDay(day, scheduleSelectedDate);
                      const isToday = isSameDay(day, scheduleToday);
                      const isDisabled = day < scheduleToday;
                      return (
                        <button
                          type="button"
                          key={day.getTime()}
                          className={`mobile-date-picker-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${isDisabled ? 'disabled' : ''}`}
                          onClick={() => handleScheduleDateSelect(day)}
                          disabled={isDisabled}
                        >
                          <span>{day.getDate()}</span>
                          {isToday && <span className="mobile-date-picker-dot"></span>}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mobile-date-picker-time-input">
                  <div className="time-input-label-row">
                    <ScheduleClockIcon size={16} />
                    <span>{t('postReview.postEdit.time.chooseHour', { defaultValue: 'Scegli orario' })}</span>
                  </div>
                  <div
                    className="time-input-wrapper"
                    onClick={openScheduleTimePicker}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openScheduleTimePicker();
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <input
                      type="time"
                      step="60"
                      value={scheduleSelectedTime || '12:00'}
                      onChange={(e) => handleScheduleTimeInputChange(e.target.value)}
                      className="time-input-field"
                      ref={scheduleTimeInputRef}
                    />
                    <div className="time-input-icon">
                      <ScheduleClockIcon size={16} aria-hidden="true" />
                    </div>

                    {isScheduleTimePickerOpen && (
                      <div className="time-picker-panel" ref={scheduleTimePanelRef}>
                        <div className="time-picker-columns">
                          <div className="time-picker-column">
                            <div className="time-picker-column-label">
                              {t('postReview.postEdit.time.hours', { defaultValue: 'Ore' })}
                            </div>
                            <div className="time-picker-scroll">
                              {scheduleHourOptions.map((hour) => {
                                const isSelected = (scheduleSelectedTime || '12:00').startsWith(hour);
                                return (
                                  <button
                                    type="button"
                                    key={hour}
                                    className={`time-picker-option ${isSelected ? 'selected' : ''}`}
                                    onClick={() => handleScheduleHourSelect(hour)}
                                  >
                                    {hour}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          <div className="time-picker-column">
                            <div className="time-picker-column-label">
                              {t('postReview.postEdit.time.minutes', { defaultValue: 'Minuti' })}
                            </div>
                            <div className="time-picker-scroll">
                              {scheduleMinuteOptions.map((minute) => {
                                const isSelected = (scheduleSelectedTime || '12:00').split(':')[1] === minute;
                                return (
                                  <button
                                    type="button"
                                    key={minute}
                                    className={`time-picker-option ${isSelected ? 'selected' : ''}`}
                                    onClick={() => handleScheduleMinuteSelect(minute)}
                                  >
                                    {minute}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>,
        portalTarget
      )}

      {showGallery && portalTarget && createPortal(
        <div className="loopino-ai-gallery-overlay" role="dialog" aria-modal="true">
          <DesktopImageGalleryPage
            onClose={closeGallery}
            onSelect={handleGallerySelect}
            viewerOrigin="desktop-ai-generator"
          />
        </div>,
        portalTarget
      )}

      {showPromptBlocksModal && (
        <AIRegenerationModal
          isOpen={showPromptBlocksModal}
          onClose={() => setShowPromptBlocksModal(false)}
          type="blocks"
          onRegenerate={handlePromptBlocksRegeneration}
          isRegenerating={isRegeneratingBlocks}
        />
      )}

      {manualAiModalOpen && (
        <AIRegenerationModal
          isOpen={manualAiModalOpen}
          onClose={() => setManualAIModalOpen(false)}
          type={manualAiModalType || 'text'}
          onRegenerate={handleManualAIModalRegenerate}
          isRegenerating={manualAiModalType === 'image' ? manualImageGenerating : descriptionLoading}
          extraContent={
            showPostGenerationSettings && manualAiModalType === 'text' ? (
              <div className="generation-settings-toggle ai-modal-context-toggles">
                <div className="loopino-ai-modal-section-title">
                  {t('imageGenerator.post.manual.generationSettings.title', { defaultValue: 'Impostazioni di generazione' })}
                </div>
                <div className="generation-toggle-row">
                  <div className="generation-toggle-title">
                    {t('imageGenerator.settings.dna.label', { defaultValue: 'Usa DNA' })}
                  </div>
                  <DualOptionToggle
                    active={useDnaForPost}
                    onChange={(next) => setUseDnaForPost(next)}
                    leftLabel={t('imageGenerator.toggle.off', { defaultValue: 'Off' })}
                    rightLabel={t('imageGenerator.toggle.on', { defaultValue: 'On' })}
                    ariaLabel={t('imageGenerator.settings.dnaToggleAria', { defaultValue: 'Attiva o disattiva il DNA per la generazione' })}
                  />
                </div>
                <div className="generation-toggle-row">
                  <div className="generation-toggle-title">
                    {t('imageGenerator.settings.strategy.label', { defaultValue: 'Usa strategia' })}
                  </div>
                  <DualOptionToggle
                    active={useStrategyForPost}
                    onChange={(next) => setUseStrategyForPost(next)}
                    leftLabel={t('imageGenerator.toggle.off', { defaultValue: 'Off' })}
                    rightLabel={t('imageGenerator.toggle.on', { defaultValue: 'On' })}
                    ariaLabel={t('imageGenerator.settings.strategyToggleAria', { defaultValue: 'Attiva o disattiva la strategia per la generazione' })}
                  />
                </div>
              </div>
            ) : null
          }
        />
      )}
    </>
  );
};

const DesktopAIGeneratorPage = (props) => (
  <DesktopAlertProvider>
    <DesktopAIGeneratorContent {...props} />
  </DesktopAlertProvider>
);

export default DesktopAIGeneratorPage;
